// src/components/MinecraftPage.jsx
import { Fragment, useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import DynMapSection from "./DynMapSection";
import {
  Trophy, Map, Skull, Flag, Crown, Zap, ClipboardCopy,
  Clock, Search, X, Info, BookOpen, ShoppingBag, ScrollText,
  Package, Users, Bell, Lightbulb, ChevronRight, ArrowRight,
  Swords, Castle, Shield, Building2, Star, ChevronDown, ChevronUp,
  AlertTriangle, Hash, Landmark, Settings, LayoutDashboard, LogOut, User,
  Menu, MessageSquare, Monitor, Mic, Download, ExternalLink,
} from "lucide-react";
import { useServerStatus } from "../hooks/useServerStatus";
import { API } from "../utils/constants";
function relTime(d) {
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const dy = Math.floor(h / 24); if (dy < 30) return `${dy}d ago`;
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}


/* ── Discord SVG (full Wumpus with eyes) ──────────────────────────────────── */
const DiscordIcon = ({ size = 14, color = "#818cf8" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

/* ── Ticket SVG ───────────────────────────────────────────────────────────── */
const TicketSVG = ({ size = 14, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
    <path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/>
  </svg>
);

/* ─────────────────────────────────────────────────────────────────────────────
   BACKGROUND IMAGE — set to any URL for a subtle full-page texture/pattern.
   ───────────────────────────────────────────────────────────────────────────── */
const MC_BG_IMAGE = "https://theonesabove.com/gallery/spawn.png";

/* ── Card shell ───────────────────────────────────────────────────────────── */
const cardStyle = {
  background: "rgba(255,255,255,.025)",
  border: "1px solid rgba(255,255,255,.07)",
  borderRadius: 12,
  overflow: "hidden",
};
const cardHead = {
  padding: "12px 16px",
  borderBottom: "1px solid rgba(255,255,255,.06)",
  fontFamily: "'Cinzel',serif",
  fontSize: 10,
  letterSpacing: 3,
  color: "rgba(168,156,200,.55)",
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
  gap: 7,
};

/* ── Factions data — static flavour only (live stats fetched from API) ─────── */
const FACTIONS = [
  {
    id:"templars", name:"The Knights Templars", shortName:"Templars",
    color:"#c0392b", colorBg:"rgba(139,26,26,.12)", colorBorder:"rgba(139,26,26,.3)",
    leader:null, members:0, territories:0, power:0, properties:0,
    description:"Warrior-monks forged in the fires of holy war. The Knights Templars are the iron fist of the Crusades — disciplined, unyielding, and bound by an oath older than memory.",
    order:"The Crusades",
    majorFactionKey:"THE_CRUSADES",
    sigil:"https://theonesabove.com/gallery/knights_templars_short.png",
  },
  {
    id:"alnurayn", name:"Alnurayn", shortName:"Alnurayn",
    color:"#ffffff", colorBg:"rgba(12,12,14,.55)", colorBorder:"rgba(80,80,80,.45)",
    leader:null, members:0, territories:0, power:0, properties:0,
    description:"Born of the golden age and baptised in blood. Their scholars map the stars; their blades find enemies in the dark — the pen and the scimitar held in the same steady hand.",
    order:"The Caliphate",
    majorFactionKey:"CALIPHATE",
    sigil:"https://theonesabove.com/gallery/alnurayn_short.png",
  },
  {
    id:"rothchilds", name:"Family of Rothchilds", shortName:"Rothchilds",
    color:"#9b7fd4", colorBg:"rgba(26,26,74,.15)", colorBorder:"rgba(155,127,212,.3)",
    leader:null, members:0, territories:0, power:0, properties:0,
    description:"They do not march armies. They move wealth. The shadow architects of the realms — kingmakers, bankers, and orchestrators of history.",
    order:"House of Rothchilds",
    majorFactionKey:"KINGDOM_OF_ZION",
    sigil:"https://theonesabove.com/gallery/rothschild_short.png",
  },
];

/* ── Leaderboard data ──────────────────────────────────────────────────────── */
const LB_CATS = [
  { id:"player_kills",     label:"Most Kills",    icon:<Skull   style={{width:13,height:13}}/>, fmt:v=>Number(v).toLocaleString() },
  { id:"deaths",           label:"Total Deaths",  icon:<Swords  style={{width:13,height:13}}/>, fmt:v=>Number(v).toLocaleString() },
  { id:"wealth",           label:"Richest",       icon:<Trophy  style={{width:13,height:13}}/>, fmt:v=>"$"+Number(v).toLocaleString() },
  { id:"playtime_minutes", label:"Most Played",   icon:<Clock   style={{width:13,height:13}}/>, fmt:v=>Math.floor(Number(v)/60)+"h" },
  { id:"blocks_broken",    label:"Blocks Broken", icon:<Package style={{width:13,height:13}}/>, fmt:v=>Number(v).toLocaleString() },
  { id:"mob_kills",        label:"Mob Kills",     icon:<Zap     style={{width:13,height:13}}/>, fmt:v=>Number(v).toLocaleString() },
  { id:"fish_caught",      label:"Fishing",       icon:<Flag    style={{width:13,height:13}}/>, fmt:v=>Number(v).toLocaleString() },
  { id:"distance_walked",  label:"Explorer",      icon:<Map     style={{width:13,height:13}}/>, fmt:v=>(Number(v)/100000).toFixed(1)+"km" },
];



/* ── Territory data ────────────────────────────────────────────────────────── */
const TERRITORIES = [
  {x:62,y:8,  w:14,h:12,faction:"templars",  name:"Fortress Dei",   color:"#c0392b"},
  {x:72,y:18, w:10,h:9, faction:"templars",  name:"Eastern Gate",   color:"#c0392b"},
  {x:65,y:32, w:8, h:8, faction:"templars",  name:"Holy Mines",     color:"#c0392b"},
  {x:74,y:6,  w:9, h:8, faction:"templars",  name:"Ironwall Fort",  color:"#c0392b"},
  {x:36,y:36, w:14,h:10,faction:"alnurayn",  name:"The Crescent",   color:"#27ae60"},
  {x:42,y:22, w:10,h:11,faction:"alnurayn",  name:"Scholar Keep",   color:"#27ae60"},
  {x:28,y:48, w:10,h:9, faction:"alnurayn",  name:"Dark Tunnels",   color:"#27ae60"},
  {x:8, y:10, w:18,h:14,faction:"rothchilds",name:"The Vault",      color:"#9b7fd4"},
  {x:4, y:26, w:10,h:12,faction:"rothchilds",name:"Banking Quarter",color:"#9b7fd4"},
  {x:14,y:22, w:10,h:6, faction:"rothchilds",name:"Trade Route",    color:"#9b7fd4"},
  {x:20,y:62, w:16,h:12,faction:"templars",  name:"Crusade Port",   color:"#c0392b"},
  {x:44,y:66, w:12,h:10,faction:"alnurayn",  name:"Silk Docks",     color:"#27ae60"},
  {x:62,y:64, w:10,h:11,faction:"rothchilds",name:"Shadow Bank",    color:"#9b7fd4"},
  {x:6, y:44, w:14,h:10,faction:"rothchilds",name:"Zion Wall",      color:"#9b7fd4"},
  {x:10,y:56, w:12,h:12,faction:"alnurayn",  name:"Oasis Town",     color:"#27ae60"},
  {x:2, y:30, w:8, h:10,faction:"templars",  name:"Quarry Keep",    color:"#c0392b"},
];

/* ── Recently-active staff ─────────────────────────────────────────────────── */
const RECENT_STAFF = [
  { name:"TOADMIN",   role:"Owner",     roleColor:"#c9a84c", skinUrl:"https://mc-heads.net/avatar/TOADMIN/40",   lastSeen:"Online" },
  { name:"DevMaster", role:"Developer", roleColor:"#f87171", skinUrl:"https://mc-heads.net/avatar/DevMaster/40", lastSeen:"4m ago" },
  { name:"ModHammer", role:"Moderator", roleColor:"#9b7fd4", skinUrl:"https://mc-heads.net/avatar/ModHammer/40", lastSeen:"22m ago" },
];

/* ── Server updates ─────────────────────────────────────────────────────────── */
/* ── Server features for About tab ─────────────────────────────────────────── */
const FEATURES = [
  {
    id:"factions", eyebrow:"Core Gameplay", title:"Survival Faction Roleplay",
    desc:"This isn't just Minecraft - it's a living historical roleplay. Join one of three legendary factions, carve out territory, and write your name into the annals of the server. Build fortresses, wage war, and forge alliances in a world where every block tells a story.",
    image:"https://theonesabove.com/gallery/factions.jpg", color:"#c0392b", imgLeft:false,
    pills:["Faction Wars","Territory Control","Roleplay Events","PvP Combat"],
  },
  {
    id:"economy", eyebrow:"Player Economy", title:"A World Driven by Wealth",
    desc:"Build your fortune through trade, conquest, and commerce. Open player shops in the marketplace, invest in resources, and outmanoeuvre rival factions financially. Every transaction ripples across the realm.",
    image:"https://theonesabove.com/gallery/image4.png", color:"#c9a84c", imgLeft:true,
    pills:["Player Shops","Trading System","Dynamic Market","In-Game Currency"],
  },
  {
    id:"cosmetics", eyebrow:"Cosmetics & Customisation", title:"Make Your Mark on the World",
    desc:"Stand out with an arsenal of cosmetics that go far beyond skins. Furnish your base with custom medieval furniture, unlock exclusive VIP items, and trail particles that light up the battlefield. Your look, your legacy.",
    image:"https://theonesabove.com/gallery/eltair.png", color:"#9b7fd4", imgLeft:false,
    pills:["Custom Furniture","VIP Items","Particles","Cosmetic Crates"],
  },
  {
    id:"voice", eyebrow:"Immersive Communication", title:"Voice Chat & Proximity Audio",
    desc:"Powered by Simple Voice Chat, hear your allies in real-time proximity audio — a whisper in the halls of your fortress, a battle cry across the field. Switch between local proximity voice and global faction channels.",
    image:null, color:"#60a5fa", imgLeft:false, icon:"🎙",
    pills:["Proximity Voice","Faction Channels","Global Chat","Local Chat"],
  },
  {
    id:"minigames", eyebrow:"Entertainment", title:"Minigames & Arena Combat",
    desc:"When the dust of faction war settles, the realm's entertainments never sleep. Test your mettle in the Colosseum — a brutal PvP arena where champions are forged — try your luck at the Casino, or explore more experiences still to come.",
    image:null, color:"#f59e0b", imgLeft:false, icon:"⚔",
    pills:["Colosseum PvP","Casino","More Coming Soon"],
  },
];

/* ── Store packages ────────────────────────────────────────────────────────── */
const STORE_PACKAGES = [
  {
    id:"noble", name:"Noble", price:"£4.99", color:"#94a3b8",
    colorBg:"rgba(148,163,184,.08)", colorBorder:"rgba(148,163,184,.2)", badge:null,
    perks:["Noble rank prefix","2x daily rewards","Custom chat colour","Noble lounge access"],
  },
  {
    id:"lord", name:"Lord", price:"£9.99", color:"#9b7fd4",
    colorBg:"rgba(123,94,167,.12)", colorBorder:"rgba(123,94,167,.3)", badge:"Popular", badgeColor:"#9b7fd4",
    perks:["Lord rank prefix","5x daily rewards","Particle effects","VIP events access","Extra faction slots"],
  },
  {
    id:"overlord", name:"Overlord", price:"£19.99", color:"#c9a84c",
    colorBg:"rgba(201,168,76,.1)", colorBorder:"rgba(201,168,76,.35)", badge:"Best Value", badgeColor:"#c9a84c",
    perks:["Overlord rank prefix","10x daily rewards","Animated name tag","Priority access","Exclusive kit","Monthly coins"],
  },
  {
    id:"coins_sm", name:"500 Coins", price:"£2.49", color:"#4ade80",
    colorBg:"rgba(74,222,128,.06)", colorBorder:"rgba(74,222,128,.2)", badge:null,
    perks:["500 TOA Coins","In-game currency","Spend at /shop"], isCoin:true,
  },
  {
    id:"coins_lg", name:"2500 Coins", price:"£9.99", color:"#4ade80",
    colorBg:"rgba(74,222,128,.06)", colorBorder:"rgba(74,222,128,.2)", badge:"Best Rate", badgeColor:"#4ade80",
    perks:["2500 TOA Coins","Bonus 10% value","In-game currency","Spend at /shop"], isCoin:true,
  },
];

/* ══════════════════════════════ SUB-COMPONENTS ══════════════════════════════ */

/* ── Map tooltip ──────────────────────────────────────────────────────────── */
function MapTooltip({territory, pos}) {
  if (!territory) return null;
  const faction = FACTIONS.find(f => f.id === territory.faction);
  return (
    <div style={{position:"absolute",left:Math.min(pos.x+12,240),top:Math.max(pos.y-30,0),background:"linear-gradient(160deg,#13102a,#0f0d20)",border:`1px solid ${territory.color}44`,borderRadius:8,padding:"8px 12px",pointerEvents:"none",zIndex:20,minWidth:140,boxShadow:`0 8px 32px rgba(0,0,0,.7)`}}>
      <div style={{fontFamily:"'Cinzel',serif",fontSize:11,color:territory.color,fontWeight:700,marginBottom:2}}>{territory.name}</div>
      <div style={{fontSize:10,color:"var(--muted)",fontFamily:"'Cinzel',serif"}}>{faction?.shortName}</div>
    </div>
  );
}

/* ── World Map ─────────────────────────────────────────────────────────────── */
function WorldMap() {
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({x:0,y:0});
  const [sel, setSel] = useState(null);
  const mapRef = useRef(null);
  const handleMM = useCallback(e => {
    const r = mapRef.current?.getBoundingClientRect();
    if (r) setTooltipPos({x:e.clientX-r.left,y:e.clientY-r.top});
  },[]);

  return (
    <div style={{position:"relative"}}>
      <div ref={mapRef} onMouseMove={handleMM} onMouseLeave={()=>setHovered(null)}
        style={{position:"relative",width:"100%",paddingBottom:"60%",background:"linear-gradient(160deg,#050d15,#080e14,#050810)",border:"1px solid rgba(74,222,128,.15)",borderRadius:12,overflow:"hidden",cursor:"crosshair"}}>
        <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",opacity:.06}} viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M55 15 L60 8 L65 15 L70 6 L75 15 L80 10 L85 18" stroke="#94a3b8" strokeWidth="1" fill="none"/>
          <path d="M0 82 Q25 78 50 82 Q75 86 100 80 L100 100 L0 100 Z" fill="#1e3a5f" opacity="0.6"/>
          <circle cx="18" cy="12" r="8" fill="#1a3a1a" opacity="0.8"/>
        </svg>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{position:"absolute",inset:0,width:"100%",height:"100%"}}>
          {TERRITORIES.map((t,i)=>{
            const ih = hovered?.name===t.name, dm = sel&&t.faction!==sel;
            return (
              <g key={i}>
                <rect x={t.x} y={t.y} width={t.w} height={t.h} fill={t.color} fillOpacity={dm?.04:ih?.55:.28} stroke={t.color} strokeWidth={ih?.4:.2} strokeOpacity={dm?.1:.8} rx={0.4} style={{transition:"all .15s",cursor:"pointer"}} onMouseEnter={()=>setHovered(t)} onMouseLeave={()=>setHovered(null)} onClick={()=>setSel(p=>p===t.faction?null:t.faction)}/>
                {(ih||t.w>12)&&!dm&&<text x={t.x+t.w/2} y={t.y+t.h/2+1.2} textAnchor="middle" fontSize="2.2" fill={t.color} fillOpacity={0.9} fontFamily="Cinzel,serif" style={{pointerEvents:"none"}}>{t.name.split(" ")[0]}</text>}
              </g>
            );
          })}
        </svg>
        <div style={{position:"absolute",bottom:10,right:10,background:"rgba(5,8,15,.85)",border:"1px solid rgba(255,255,255,.08)",borderRadius:8,padding:"8px 10px",display:"flex",flexDirection:"column",gap:4}}>
          {FACTIONS.map(f=>(
            <div key={f.id} onClick={()=>setSel(p=>p===f.id?null:f.id)} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",opacity:sel&&sel!==f.id?.35:1,transition:".15s"}}>
              <div style={{width:8,height:8,borderRadius:2,background:f.color,flexShrink:0}}/>
              <span style={{fontSize:9,fontFamily:"'Cinzel',serif",color:f.color,letterSpacing:.5}}>{f.shortName}</span>
            </div>
          ))}
        </div>
        <div style={{position:"absolute",top:10,right:10,width:32,height:32}}>
          <svg viewBox="0 0 32 32" width="32" height="32" style={{opacity:0.4}}>
            <circle cx="16" cy="16" r="14" fill="none" stroke="#4ade80" strokeWidth="0.5"/>
            <polygon points="16,2 18,14 16,12 14,14" fill="#f87171"/>
            <polygon points="16,30 18,18 16,20 14,18" fill="#94a3b8"/>
            <polygon points="2,16 14,14 12,16 14,18" fill="#94a3b8"/>
            <polygon points="30,16 18,14 20,16 18,18" fill="#94a3b8"/>
            <text x="16" y="6" textAnchor="middle" fontSize="3.5" fill="#f87171" fontFamily="Cinzel,serif">N</text>
          </svg>
        </div>
        <div style={{position:"absolute",top:10,left:10,fontFamily:"'Cinzel Decorative',serif",fontSize:10,color:"rgba(74,222,128,.5)",letterSpacing:2}}>REALM MAP</div>
        {hovered&&<MapTooltip territory={hovered} pos={tooltipPos}/>}
      </div>
      {sel&&<div style={{marginTop:8,textAlign:"center"}}><button onClick={()=>setSel(null)} style={{background:"none",border:"1px solid var(--border)",borderRadius:6,color:"var(--muted)",fontSize:10,fontFamily:"'Cinzel',serif",padding:"4px 12px",cursor:"pointer",letterSpacing:1}}>Clear Filter</button></div>}
    </div>
  );
}

/* ── Rank badge ────────────────────────────────────────────────────────────── */
const MEDAL_COLORS = ["#f0d878","#c0c0d0","#cd7f32"];
function RankBadge({rank}) {
  if (rank <= 3) return (
    <div style={{width:22,height:22,borderRadius:"50%",background:`radial-gradient(circle at 35% 35%,${MEDAL_COLORS[rank-1]},${MEDAL_COLORS[rank-1]}88)`,boxShadow:`0 0 8px ${MEDAL_COLORS[rank-1]}55`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
      <Crown style={{width:10,height:10,color:"#0a0a0f"}}/>
    </div>
  );
  return <span style={{fontFamily:"'Cinzel',serif",fontSize:11,fontWeight:700,color:"var(--muted)",width:22,textAlign:"center",display:"inline-block"}}>{rank}</span>;
}

/* ── Player head ───────────────────────────────────────────────────────────── */
function PlayerHead({uuid, name, size=32, fc}) {
  const border = fc ? `1px solid ${fc}44` : "1px solid rgba(255,255,255,.1)";
  if (uuid) return (
    <img
      src={`https://crafthead.net/avatar/${uuid}/${size}`}
      alt={name}
      onError={e=>{e.currentTarget.onerror=null;e.currentTarget.src=`https://mc-heads.net/avatar/MHF_Steve/${size}`;}}
      style={{width:size,height:size,borderRadius:4,flexShrink:0,imageRendering:"pixelated",border}}
    />
  );
  return (
    <div style={{width:size,height:size,borderRadius:4,flexShrink:0,background:"rgba(155,127,212,.15)",border,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Cinzel',serif",fontSize:Math.floor(size*0.38),fontWeight:700,color:"#9b7fd4"}}>
      {(name?.[0]??"?").toUpperCase()}
    </div>
  );
}

/* ── Stat pill used inside the player modal ───────────────────────────────── */
function StatPill({label, value, icon, color}) {
  return (
    <div style={{background:`${color}0d`,border:`1px solid ${color}28`,borderRadius:8,padding:"10px 12px",display:"flex",flexDirection:"column",gap:3}}>
      <div style={{display:"flex",alignItems:"center",gap:5,color:`${color}99`,marginBottom:1}}>{icon}</div>
      <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:13,fontWeight:700,color,lineHeight:1}}>{value}</div>
      <div style={{fontSize:9,color:"var(--muted)",fontFamily:"'Cinzel',serif",letterSpacing:1,textTransform:"uppercase"}}>{label}</div>
    </div>
  );
}

/* ── Player stats modal ────────────────────────────────────────────────────── */
function PlayerModal({player, onClose}) {
  if (!player) return null;
  const fc   = player.factionColor ?? "#9b7fd4";
  const gold = "#c9a84c";

  const pvpDeaths = player.pvpDeaths ?? player.deaths ?? 0;
  const kdr = pvpDeaths > 0
    ? (player.playerKills / pvpDeaths).toFixed(2)
    : player.playerKills > 0 ? (player.playerKills).toFixed(2) : "—";

  const fmt  = n => Number(n??0).toLocaleString();
  const fmtH = m => Math.floor((m??0)/60)+"h "+(((m??0)%60))+"m";
  const fmtKm= v => ((v??0)/100000).toFixed(1)+"km";

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:500,background:"rgba(0,0,0,.75)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflowY:"auto"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:520,maxHeight:"calc(100vh - 180px)",overflowY:"auto",background:"linear-gradient(160deg,#0f0c1a,#0a0810,#0d0c14)",border:`1px solid ${fc}40`,borderRadius:14,boxShadow:`0 0 60px ${fc}22, 0 24px 80px rgba(0,0,0,.8)`,position:"relative"}}>

        {/* Corner ornaments */}
        <svg style={{position:"absolute",top:10,left:10,opacity:.4,pointerEvents:"none"}} width={20} height={20} viewBox="0 0 20 20" fill="none">
          <path d="M2 18 L2 2 L18 2" stroke={gold} strokeWidth="1.5"/>
        </svg>
        <svg style={{position:"absolute",top:10,right:10,opacity:.4,pointerEvents:"none"}} width={20} height={20} viewBox="0 0 20 20" fill="none">
          <path d="M18 18 L18 2 L2 2" stroke={gold} strokeWidth="1.5"/>
        </svg>
        <svg style={{position:"absolute",bottom:10,left:10,opacity:.4,pointerEvents:"none"}} width={20} height={20} viewBox="0 0 20 20" fill="none">
          <path d="M2 2 L2 18 L18 18" stroke={gold} strokeWidth="1.5"/>
        </svg>
        <svg style={{position:"absolute",bottom:10,right:10,opacity:.4,pointerEvents:"none"}} width={20} height={20} viewBox="0 0 20 20" fill="none">
          <path d="M18 2 L18 18 L2 18" stroke={gold} strokeWidth="1.5"/>
        </svg>

        {/* Header */}
        <div style={{padding:"24px 24px 18px",borderBottom:`1px solid ${fc}20`,position:"relative"}}>
          <button onClick={onClose} style={{position:"absolute",top:16,right:16,background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:6,color:"var(--muted)",cursor:"pointer",padding:"4px 6px",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <X style={{width:13,height:13}}/>
          </button>

          <div style={{display:"flex",alignItems:"center",gap:16}}>
            {/* Big head */}
            <div style={{position:"relative",flexShrink:0}}>
              <PlayerHead uuid={player.uuid} name={player.name} size={64} fc={fc}/>
              {player.isOnline && (
                <div style={{position:"absolute",bottom:2,right:2,width:10,height:10,borderRadius:"50%",background:"#4ade80",border:"2px solid #0a0810",boxShadow:"0 0 6px #4ade8099"}}/>
              )}
            </div>

            <div style={{flex:1,minWidth:0}}>
              {/* Online badge */}
              {player.isOnline && (
                <div style={{display:"inline-flex",alignItems:"center",gap:4,background:"rgba(74,222,128,.1)",border:"1px solid rgba(74,222,128,.3)",borderRadius:20,padding:"2px 8px",marginBottom:6}}>
                  <div style={{width:5,height:5,borderRadius:"50%",background:"#4ade80"}}/>
                  <span style={{fontSize:8,fontFamily:"'Cinzel',serif",letterSpacing:2,color:"#4ade80",textTransform:"uppercase"}}>Online</span>
                </div>
              )}
              <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:20,fontWeight:900,color:"var(--text)",lineHeight:1.1,marginBottom:4}}>{player.name}</div>
              {player.faction
                ? <div style={{display:"inline-flex",alignItems:"center",gap:5,background:`${fc}15`,border:`1px solid ${fc}30`,borderRadius:4,padding:"3px 10px"}}>
                    <div style={{width:5,height:5,borderRadius:1,background:fc,flexShrink:0}}/>
                    <span style={{fontFamily:"'Cinzel',serif",fontSize:10,letterSpacing:2,color:fc,textTransform:"uppercase",fontWeight:700}}>{player.faction}</span>
                  </div>
                : <div style={{fontFamily:"'Cinzel',serif",fontSize:10,color:"var(--muted)",letterSpacing:1}}>No faction</div>
              }
            </div>
          </div>

          {/* KDR highlight bar */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginTop:18}}>
            {[
              {label:"Player Kills", value:fmt(player.playerKills), color:"#c0392b"},
              {label:"K/D Ratio",    value:kdr,                      color:gold},
              {label:"PvP Deaths",   value:fmt(pvpDeaths),           color:"#9b7fd4"},
            ].map(s=>(
              <div key={s.label} style={{textAlign:"center",padding:"10px 6px",background:"rgba(0,0,0,.3)",borderRadius:8,border:`1px solid ${s.color}22`}}>
                <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:16,fontWeight:900,color:s.color,lineHeight:1}}>{s.value}</div>
                <div style={{fontSize:8,fontFamily:"'Cinzel',serif",letterSpacing:1.5,color:"var(--muted)",textTransform:"uppercase",marginTop:4}}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Body — stat sections */}
        <div style={{padding:"18px 24px 28px",display:"flex",flexDirection:"column",gap:18}}>

          {/* Economy */}
          <div>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:8,letterSpacing:4,color:gold,textTransform:"uppercase",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
              <div style={{flex:1,height:"1px",background:`linear-gradient(to right,${gold}40,transparent)`}}/>
              Economy
              <div style={{flex:1,height:"1px",background:`linear-gradient(to left,${gold}40,transparent)`}}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              <StatPill label="Wealth"  value={"$"+fmt(player.wealth)}  icon={<Trophy style={{width:11,height:11}}/>} color={gold}/>
              <StatPill label="Pocket"  value={"$"+fmt(player.pocket)}  icon={<Package style={{width:11,height:11}}/>} color={gold}/>
              <StatPill label="Bank"    value={"$"+fmt(player.bank)}    icon={<Landmark style={{width:11,height:11}}/>} color={gold}/>
            </div>
          </div>

          {/* Combat */}
          <div>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:8,letterSpacing:4,color:"#c0392b",textTransform:"uppercase",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
              <div style={{flex:1,height:"1px",background:`linear-gradient(to right,#c0392b40,transparent)`}}/>
              Combat
              <div style={{flex:1,height:"1px",background:`linear-gradient(to left,#c0392b40,transparent)`}}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              <StatPill label="Mob Kills"    value={fmt(player.mobKills)}                         icon={<Skull   style={{width:11,height:11}}/>} color="#c0392b"/>
              <StatPill label="PvP Deaths"   value={fmt(pvpDeaths)}                               icon={<Swords  style={{width:11,height:11}}/>} color="#c0392b"/>
              <StatPill label="Total Deaths" value={fmt(player.deaths??0)}                        icon={<Skull   style={{width:11,height:11}}/>} color="#9b7fd4"/>
              <StatPill label="Dmg Dealt"    value={fmt(Math.round((player.damageDealt??0)/2))}   icon={<Swords  style={{width:11,height:11}}/>} color="#c0392b"/>
              <StatPill label="Arrows Shot"  value={fmt(player.arrowsShot)}                       icon={<Zap     style={{width:11,height:11}}/>} color="#c0392b"/>
            </div>
          </div>

          {/* World */}
          <div>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:8,letterSpacing:4,color:"#60a5fa",textTransform:"uppercase",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
              <div style={{flex:1,height:"1px",background:`linear-gradient(to right,#60a5fa40,transparent)`}}/>
              World
              <div style={{flex:1,height:"1px",background:`linear-gradient(to left,#60a5fa40,transparent)`}}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              <StatPill label="Blocks Broken" value={fmt(player.blocksBroken)}       icon={<Package style={{width:11,height:11}}/>} color="#60a5fa"/>
              <StatPill label="Blocks Placed" value={fmt(player.blocksPlaced)}       icon={<Castle  style={{width:11,height:11}}/>} color="#60a5fa"/>
              <StatPill label="Fish Caught"   value={fmt(player.fishCaught)}         icon={<Flag    style={{width:11,height:11}}/>} color="#60a5fa"/>
            </div>
          </div>

          {/* Life */}
          <div>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:8,letterSpacing:4,color:"#4ade80",textTransform:"uppercase",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
              <div style={{flex:1,height:"1px",background:`linear-gradient(to right,#4ade8040,transparent)`}}/>
              Life &amp; Travel
              <div style={{flex:1,height:"1px",background:`linear-gradient(to left,#4ade8040,transparent)`}}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              <StatPill label="Playtime"    value={fmtH(player.playtimeMinutes)}     icon={<Clock style={{width:11,height:11}}/>} color="#4ade80"/>
              <StatPill label="Dist. Walked" value={fmtKm(player.distanceWalked)}   icon={<Map   style={{width:11,height:11}}/>} color="#4ade80"/>
              <StatPill label="Jumps"       value={fmt(player.jumps??0)}             icon={<Zap   style={{width:11,height:11}}/>} color="#4ade80"/>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ── Leaderboard ───────────────────────────────────────────────────────────── */
function Leaderboard({onModalChange}) {
  const [cat,         setCat]        = useState(LB_CATS[0].id);
  const [entries,     setEntries]    = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [error,       setError]      = useState(null);
  const [q,           setQ]          = useState("");
  const [results,     setResults]    = useState(null);
  const [searching,   setSearching]  = useState(false);
  const [modalPlayer, setModalPlayer]= useState(null);
  const [fetchingPlayer, setFetchingPlayer] = useState(false);
  const openModal = (p) => { setModalPlayer(p); onModalChange&&onModalChange(true); };
  const searchTimer                   = useRef(null);
  const isSearching                   = q.trim().length > 0;

  // Fetch leaderboard whenever category changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        const res = await fetch(`${API}/tracker/leaderboard/${cat}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setEntries(data.leaderboard ?? []);
      } catch(e) {
        if (!cancelled) setError("Could not load leaderboard.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cat]);

  // Live debounced search
  const handleSearch = useCallback(v => {
    setQ(v);
    clearTimeout(searchTimer.current);
    if (!v.trim()) { setResults(null); setSearching(false); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/tracker/search?q=${encodeURIComponent(v.trim())}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        setResults(data.players ?? []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  // Fetch full player stats by name then open modal
  const fetchAndOpenPlayer = useCallback(async (partialPlayer) => {
    if (fetchingPlayer) return;
    // If from search, player already has full stats
    if (partialPlayer.playerKills !== undefined) {
      openModal(partialPlayer);
      return;
    }
    setFetchingPlayer(true);
    try {
      const res = await fetch(`${API}/tracker/search?q=${encodeURIComponent(partialPlayer.name)}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      // Find exact match
      const full = (data.players ?? []).find(p =>
        p.name?.toLowerCase() === partialPlayer.name?.toLowerCase()
      ) ?? data.players?.[0] ?? partialPlayer;
      openModal(full);
    } catch {
      openModal(partialPlayer);
    } finally {
      setFetchingPlayer(false);
    }
  }, [fetchingPlayer]);

  const catMeta = LB_CATS.find(c => c.id === cat);

  return (
    <div>
      {/* Player stats modal */}
      {modalPlayer && <PlayerModal player={modalPlayer} onClose={()=>{setModalPlayer(null);onModalChange&&onModalChange(false);}}/>}

      {/* Search bar */}
      <div style={{marginBottom:20,position:"relative"}}>
        <div style={{
          display:"flex",alignItems:"center",gap:10,
          background: isSearching ? "rgba(201,168,76,.06)" : "rgba(10,8,20,.7)",
          border:`1px solid ${isSearching ? "rgba(201,168,76,.5)" : "rgba(201,168,76,.18)"}`,
          borderRadius:10,padding:"12px 16px",transition:"all .25s",
          boxShadow: isSearching ? "0 0 24px rgba(201,168,76,.1), inset 0 0 12px rgba(201,168,76,.04)" : "none"
        }}>
          <Search style={{width:14,height:14,color:isSearching?"#c9a84c":"var(--muted)",flexShrink:0,transition:".2s"}}/>
          <input
            value={q}
            onChange={e=>handleSearch(e.target.value)}
            placeholder="Search any player by name…"
            style={{flex:1,background:"none",border:"none",outline:"none",color:"var(--text)",fontSize:13,fontFamily:"'Cinzel',serif",letterSpacing:.3}}
          />
          {(searching || fetchingPlayer) && (
            <div style={{width:12,height:12,borderRadius:"50%",border:"2px solid rgba(201,168,76,.2)",borderTopColor:"#c9a84c",animation:"spin .7s linear infinite",flexShrink:0}}/>
          )}
          {q && !(searching || fetchingPlayer) && (
            <button onClick={()=>handleSearch("")} style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.08)",borderRadius:4,cursor:"pointer",color:"var(--muted)",padding:"2px 4px",display:"flex",transition:".15s"}}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.1)"}
              onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,.05)"}
            >
              <X style={{width:12,height:12}}/>
            </button>
          )}
        </div>

        {/* Search dropdown — command-palette */}
        {q && results !== null && (
          <div style={{
            position:"absolute",top:"calc(100% + 6px)",left:0,right:0,zIndex:50,
            background:"rgba(9,7,16,.97)",
            border:"1px solid rgba(255,255,255,.08)",
            borderRadius:12,overflow:"hidden",
            backdropFilter:"blur(24px)",
            boxShadow:"0 24px 80px rgba(0,0,0,.9), 0 0 0 1px rgba(255,255,255,.04), inset 0 1px 0 rgba(255,255,255,.06)"
          }}>
            <div style={{maxHeight:340,overflowY:"auto",padding:"6px"}}>
              {results.length === 0
                ? (
                  <div style={{padding:"28px 20px",textAlign:"center"}}>
                    <div style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"rgba(255,255,255,.25)",letterSpacing:2}}>No results for "{q}"</div>
                  </div>
                )
                : results.map((p, i) => {
                    const fc    = p.factionColor ?? null;
                    const kills = p.playerKills ?? 0;
                    const kdr   = p.deaths > 0 ? (kills/p.deaths).toFixed(1) : kills > 0 ? "∞" : "—";
                    return (
                      <div
                        key={p.uuid ?? p.name}
                        onClick={() => { openModal(p); handleSearch(""); }}
                        style={{display:"flex",alignItems:"center",gap:10,padding:"9px 10px",cursor:"pointer",borderRadius:8,transition:"background .12s",position:"relative"}}
                        onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.06)"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                      >
                        <div style={{position:"relative",flexShrink:0}}>
                          <PlayerHead uuid={p.uuid} name={p.name} size={38} fc={fc}/>
                          {p.isOnline && <div style={{position:"absolute",bottom:-1,right:-1,width:8,height:8,borderRadius:"50%",background:"#4ade80",border:"2px solid rgba(9,7,16,.97)",boxShadow:"0 0 5px #4ade8080"}}/>}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontFamily:"'Inter',sans-serif",fontSize:13,fontWeight:600,color:"rgba(255,255,255,.92)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",lineHeight:1.3,marginBottom:2}}>{p.name}</div>
                          <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                            {fc ? (
                              <span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:9,fontFamily:"'Cinzel',serif",letterSpacing:1,color:"rgba(255,255,255,.45)",textTransform:"uppercase"}}>
                                <span style={{width:5,height:5,borderRadius:"50%",background:fc,flexShrink:0,boxShadow:`0 0 4px ${fc}`}}/>
                                {p.faction}
                              </span>
                            ) : (
                              <span style={{fontSize:9,color:"rgba(255,255,255,.2)",fontFamily:"'Cinzel',serif",letterSpacing:1}}>No faction</span>
                            )}
                            <span style={{fontSize:9,color:"rgba(255,255,255,.18)",fontFamily:"'Inter',sans-serif"}}>·</span>
                            <span style={{fontSize:9,color:"rgba(255,255,255,.3)",fontFamily:"'Inter',sans-serif"}}>{kills.toLocaleString()} kills</span>
                            <span style={{fontSize:9,color:"rgba(255,255,255,.18)",fontFamily:"'Inter',sans-serif"}}>·</span>
                            <span style={{fontSize:9,color:"rgba(255,255,255,.3)",fontFamily:"'Inter',sans-serif"}}>KD {kdr}</span>
                          </div>
                        </div>
                        <ChevronRight style={{width:14,height:14,color:"rgba(255,255,255,.25)",flexShrink:0}}/>
                      </div>
                    );
                  })
              }
            </div>
          </div>
        )}
      </div>

      {/* Category tabs + leaderboard table — hidden while searching */}
      {!isSearching && <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap"}}>
        {LB_CATS.map(c=>(
          <button key={c.id} onClick={()=>setCat(c.id)} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 14px",borderRadius:3,cursor:"pointer",background:cat===c.id?"linear-gradient(135deg,#7b5ea7,#5a3d8a)":"rgba(255,255,255,.03)",color:cat===c.id?"#fff":"var(--muted)",fontFamily:"'Cinzel',serif",fontSize:9,fontWeight:700,letterSpacing:1.2,textTransform:"uppercase",transition:".15s",border:cat===c.id?"1px solid #7b5ea7":"1px solid rgba(255,255,255,.08)",boxShadow:cat===c.id?"0 0 12px rgba(123,94,167,.3)":"none"}}>
            {c.icon}{c.label}
          </button>
        ))}
      </div>}

      {isSearching && (
        <div style={{padding:"20px 0 8px",display:"flex",alignItems:"center",gap:10}}>
          <div style={{flex:1,height:"1px",background:"linear-gradient(to right,rgba(201,168,76,.15),transparent)"}}/>
          <span style={{fontSize:9,fontFamily:"'Cinzel',serif",letterSpacing:3,color:"rgba(201,168,76,.5)",textTransform:"uppercase"}}>Searching players…</span>
          <div style={{flex:1,height:"1px",background:"linear-gradient(to left,rgba(201,168,76,.15),transparent)"}}/>
        </div>
      )}

      {/* Error */}
      {!isSearching && error && (
        <div style={{marginBottom:12,padding:"8px 12px",borderRadius:8,background:"rgba(192,57,43,.1)",border:"1px solid rgba(192,57,43,.3)",color:"#e87070",fontSize:12,display:"flex",alignItems:"center",gap:7}}>
          <AlertTriangle style={{width:13,height:13,flexShrink:0}}/> {error}
        </div>
      )}

      {/* Leaderboard table — hidden while searching */}
      {!isSearching && <div style={{background:"rgba(255,255,255,.01)",border:"1px solid rgba(201,168,76,.1)",borderRadius:10,overflow:"hidden"}}>
        {/* Table header */}
        <div style={{display:"grid",gridTemplateColumns:"42px 38px 1fr 110px",gap:0,padding:"8px 14px",background:"rgba(201,168,76,.04)",borderBottom:"1px solid rgba(201,168,76,.1)"}}>
          {["#","","Player",catMeta?.label ?? "Score"].map((h,i)=>(
            <div key={i} style={{fontFamily:"'Cinzel',serif",fontSize:8,letterSpacing:2.5,color:"rgba(201,168,76,.6)",textTransform:"uppercase",textAlign:i===3?"right":"left"}}>{h}</div>
          ))}
        </div>

        {loading
          ? [...Array(10)].map((_,i)=>(
              <div key={i} style={{display:"grid",gridTemplateColumns:"42px 38px 1fr 110px",gap:0,padding:"11px 14px",borderBottom:"1px solid rgba(255,255,255,.03)",alignItems:"center"}}>
                <div style={{width:22,height:22,borderRadius:"50%",background:"rgba(255,255,255,.05)"}}/>
                <div style={{width:32,height:32,borderRadius:4,background:"rgba(255,255,255,.05)"}}/>
                <div>
                  <div style={{width:"45%",height:10,borderRadius:3,background:"rgba(255,255,255,.06)",marginBottom:5}}/>
                  <div style={{width:"28%",height:8,borderRadius:3,background:"rgba(255,255,255,.04)"}}/>
                </div>
                <div style={{width:55,height:12,borderRadius:3,background:"rgba(255,255,255,.05)",marginLeft:"auto"}}/>
              </div>
            ))
          : entries.length === 0
            ? <div style={{padding:"32px",textAlign:"center",color:"var(--muted)",fontSize:12,fontFamily:"'Cinzel',serif",letterSpacing:1}}>No data available yet.</div>
            : entries.map((e, i) => {
                const fc  = e.factionColor ?? "#6b7280";
                const isTop = i < 3;
                return (
                  <div
                    key={e.uuid ?? e.name}
                    onClick={() => fetchAndOpenPlayer(e)}
                    style={{display:"grid",gridTemplateColumns:"42px 38px 1fr 110px",gap:0,padding:"10px 14px",borderBottom:i<entries.length-1?"1px solid rgba(255,255,255,.03)":"none",alignItems:"center",cursor:"pointer",background:isTop?`linear-gradient(90deg,${fc}0a,transparent)`:"transparent",transition:"background .15s"}}
                    onMouseEnter={ex=>ex.currentTarget.style.background=isTop?`linear-gradient(90deg,${fc}18,rgba(255,255,255,.02))`:"rgba(255,255,255,.02)"}
                    onMouseLeave={ex=>ex.currentTarget.style.background=isTop?`linear-gradient(90deg,${fc}0a,transparent)`:"transparent"}
                  >
                    {/* Rank */}
                    <div style={{display:"flex",justifyContent:"flex-start"}}><RankBadge rank={e.rank}/></div>
                    {/* Head */}
                    <PlayerHead uuid={e.uuid} name={e.name} size={32} fc={fc}/>
                    {/* Name + faction */}
                    <div style={{minWidth:0,paddingLeft:10}}>
                      <div style={{fontFamily:"'Cinzel',serif",fontSize:12,fontWeight:700,color:isTop?"var(--text)":"var(--text2)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{e.name}</div>
                      {e.faction
                        ? <div style={{fontSize:9,color:fc,fontFamily:"'Cinzel',serif",letterSpacing:1.5,textTransform:"uppercase",fontWeight:600,marginTop:2}}>{e.faction}</div>
                        : <div style={{fontSize:9,color:"var(--muted)",fontFamily:"'Cinzel',serif",marginTop:2}}>—</div>
                      }
                    </div>
                    {/* Value */}
                    <div style={{textAlign:"right"}}>
                      <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:13,fontWeight:700,color:i===0?"#f0d878":i===1?"#c0c0d0":i===2?"#cd7f32":"var(--text2)",lineHeight:1}}>{catMeta ? catMeta.fmt(e.value) : e.value}</div>
                      <div style={{fontSize:8,color:"var(--muted)",fontFamily:"'Cinzel',serif",letterSpacing:1,textTransform:"uppercase",marginTop:3,opacity:.7}}>{catMeta?.label}</div>
                    </div>
                  </div>
                );
              })
        }
      </div>}
      {!isSearching && <div style={{marginTop:8,textAlign:"center"}}>
        <span style={{fontSize:9,color:"var(--muted)",fontFamily:"'Cinzel',serif",letterSpacing:1.5,textTransform:"uppercase",opacity:.6}}>Click any row to view full player stats</span>
      </div>}
    </div>
  );
}

/* ── Factions tab — live data from API ─────────────────────────────────────── */
function FactionsTab({expandedFaction, setExpandedFaction}) {
  const [liveData, setLiveData] = useState(null);
  const [roster,   setRoster]   = useState({});   // { majorFactionKey: [{ playerName, rank }] }
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API}/factions/map`, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setLiveData(data);

        // Build roster grouped by majorFaction
        const rosterMap = {};
        for (const f of (data.factions ?? [])) {
          const key = f.majorFaction;
          if (!rosterMap[key]) rosterMap[key] = [];
          if (Array.isArray(f.members)) {
            rosterMap[key].push(...f.members);
          }
        }
        setRoster(rosterMap);
      } catch(e) {
        if (e.name !== "AbortError") setError("Could not load live faction data.");
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  // Merge static flavour with live DB stats
  const merged = FACTIONS.map(f => {
    const live = liveData?.factions?.filter(lf => lf.majorFaction === f.majorFactionKey) ?? [];
    const members     = live.reduce((s, lf) => s + (lf.memberCount    ?? 0), 0);
    const territories = live.reduce((s, lf) => s + (lf.territoryCount ?? 0), 0);
    const properties  = live.reduce((s, lf) => s + (lf.propertyCount  ?? 0), 0);
    const power       = live.reduce((s, lf) => s + (lf.totalPower     ?? 0), 0);

    // Pick color from first live faction if available
    const liveColor = live[0]?.mapColor ?? null;

    // Find leader from roster
    const rosterMembers = roster[f.majorFactionKey] ?? [];
    const leaderEntry   = rosterMembers.find(m => m.rank === "LEADER" || m.rank === "Leader");
    const liveLeader    = leaderEntry?.playerName ?? null;

    return { ...f, members, territories, properties, power,
             color: liveColor ?? f.color,
             leader: liveLeader };
  });

  const summary = liveData?.summary;
  const totalFactions    = summary?.totalFactions    ?? FACTIONS.length;
  const totalTerritories = summary?.totalTerritories ?? 0;
  const totalProperties  = summary?.totalProperties  ?? 0;
  const totalPower       = merged.reduce((s,f) => s + f.power, 0);

  return (
    <div>
      <div style={{marginBottom:20}}>
        <div style={{fontFamily:"'Cinzel',serif",fontSize:10,letterSpacing:5,color:"#4ade80",textTransform:"uppercase",marginBottom:4}}>Active Powers</div>
        <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:22,color:"var(--text)",marginBottom:4}}>Factions</div>
        <div style={{fontSize:13,color:"var(--muted)"}}>Click a faction to see their full roster, stats, and territories.</div>
      </div>

      {/* Summary pills */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:10,marginBottom:24}}>
        {[
          {label:"Factions",    value: loading ? "…" : totalFactions,                    icon:<Swords    style={{width:15,height:15}}/>},
          {label:"Territories", value: loading ? "…" : totalTerritories.toLocaleString(), icon:<Castle    style={{width:15,height:15}}/>},
          {label:"Properties",  value: loading ? "…" : totalProperties.toLocaleString(),  icon:<Building2 style={{width:15,height:15}}/>},
          {label:"Total Power", value: loading ? "…" : totalPower.toLocaleString()+" blk²", icon:<Zap   style={{width:15,height:15}}/>},
        ].map(s=>(
          <div key={s.label} style={{background:"rgba(255,255,255,.02)",border:"1px solid var(--border)",borderRadius:8,padding:"12px 14px",textAlign:"center"}}>
            <div style={{display:"flex",justifyContent:"center",marginBottom:4,color:"var(--muted)"}}>{s.icon}</div>
            <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:16,color:"var(--gold)",fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.value}</div>
            <div style={{fontSize:9,color:"var(--muted)",fontFamily:"'Cinzel',serif",letterSpacing:.5,textTransform:"uppercase"}}>{s.label}</div>
          </div>
        ))}
      </div>

      {error && (
        <div style={{marginBottom:12,padding:"8px 12px",borderRadius:8,background:"rgba(192,57,43,.1)",border:"1px solid rgba(192,57,43,.3)",color:"#e87070",fontSize:12,display:"flex",alignItems:"center",gap:7}}>
          <AlertTriangle style={{width:13,height:13,flexShrink:0}}/> {error} Showing last known values.
        </div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {merged.map(f=>(
          <FactionCard key={f.id} faction={f} loading={loading}
            roster={roster[f.majorFactionKey] ?? []}
            expanded={expandedFaction===f.id}
            onToggle={()=>setExpandedFaction(p=>p===f.id?null:f.id)}/>
        ))}
      </div>
    </div>
  );
}


/* ── Faction card ──────────────────────────────────────────────────────────── */
function FactionCard({faction, expanded, onToggle, loading, roster}) {
  const RANK_ICON = {
    LEADER:  <Crown  style={{width:10,height:10}}/>,
    OFFICER: <Shield style={{width:10,height:10}}/>,
    MEMBER:  <Users  style={{width:10,height:10}}/>,
  };
  const RANK_LABEL = { LEADER:"Leader", OFFICER:"Officer", MEMBER:"Member" };
  const RANK_ORDER = { LEADER:0, OFFICER:1, MEMBER:2 };
  const sortedRoster = [...(roster??[])].sort((a,b)=>
    (RANK_ORDER[(a.rank??"").toUpperCase()]??3)-(RANK_ORDER[(b.rank??"").toUpperCase()]??3)
  );

  return (
    <div onClick={onToggle} style={{background:faction.colorBg,border:`1px solid ${expanded?faction.color+"55":faction.colorBorder}`,borderRadius:10,padding:"14px 16px",cursor:"pointer",transition:".2s",boxShadow:expanded?`0 0 24px ${faction.color}18`:"none"}}>
      {/* ── Header ── */}
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <div style={{width:44,height:44,borderRadius:8,flexShrink:0,background:`linear-gradient(135deg,${faction.color}22,${faction.color}08)`,border:`1px solid ${faction.colorBorder}`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
          {faction.sigil
            ? <img src={faction.sigil} alt={faction.shortName} style={{width:"100%",height:"100%",objectFit:"contain",padding:4}}/>
            : <Flag style={{width:20,height:20,color:faction.color}}/>}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontFamily:"'Cinzel',serif",fontSize:9,letterSpacing:3,color:faction.id==="alnurayn"?"#ffffff":faction.color,textTransform:"uppercase",marginBottom:2,textShadow:faction.id==="alnurayn"?"0 0 2px #000, 0 0 6px #000, 0 0 10px #000, 2px 2px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 3px 0 0 #000, -3px 0 0 #000, 0 3px 0 #000, 0 -3px 0 #000, 0 4px 14px rgba(0,0,0,1), 0 0 28px rgba(0,0,0,.9)":undefined}}>{faction.order}</div>
          <div style={{fontFamily:"'Cinzel',serif",fontSize:13,fontWeight:700,color:faction.id==="alnurayn"?"#ffffff":faction.color,textShadow:faction.id==="alnurayn"?"0 0 2px #000, 0 0 6px #000, 0 0 12px #000, 2px 2px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 3px 0 0 #000, -3px 0 0 #000, 0 3px 0 #000, 0 -3px 0 #000, 0 0 20px rgba(0,0,0,1), 0 0 40px rgba(0,0,0,.8)":undefined}}>{faction.name}</div>
          <div style={{fontSize:11,color:"var(--muted)",marginTop:2,display:"flex",alignItems:"center",gap:5}}>
            <Crown style={{width:10,height:10,color:faction.color,flexShrink:0}}/>
            {loading
              ? <span style={{opacity:.4}}>Loading…</span>
              : <span>Led by <span style={{color:"var(--text2)",fontWeight:600}}>{faction.leader ?? "N/A"}</span></span>}
          </div>
        </div>
        <div style={{color:"var(--muted)",flexShrink:0}}>
          {expanded ? <ChevronUp style={{width:14,height:14}}/> : <ChevronDown style={{width:14,height:14}}/>}
        </div>
      </div>

      {/* ── Expanded panel ── */}
      {expanded && (
        <div style={{marginTop:14,paddingTop:14,borderTop:`1px solid ${faction.colorBorder}`}} onClick={e=>e.stopPropagation()}>
          <p style={{fontSize:12,color:"var(--text2)",lineHeight:1.65,marginBottom:14,fontStyle:"italic"}}>"{faction.description}"</p>

          {/* Stat grid */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:16}}>
            {loading
              ? [0,1,2,3].map(i=>(
                  <div key={i} style={{background:"rgba(0,0,0,.2)",borderRadius:6,padding:"8px",textAlign:"center"}}>
                    <div style={{width:32,height:10,borderRadius:4,background:`${faction.color}20`,margin:"0 auto 6px"}}/>
                    <div style={{width:48,height:14,borderRadius:4,background:`${faction.color}18`,margin:"0 auto 4px"}}/>
                    <div style={{width:36,height:8,borderRadius:4,background:`${faction.color}12`,margin:"0 auto"}}/>
                  </div>
                ))
              : [
                  {label:"Territories", value:faction.territories.toLocaleString(),      icon:<Castle    style={{width:11,height:11}}/>},
                  {label:"Properties",  value:faction.properties.toLocaleString(),        icon:<Building2 style={{width:11,height:11}}/>},
                  {label:"Total Power", value:faction.power.toLocaleString()+" blk²",    icon:<Zap       style={{width:11,height:11}}/>},
                  {label:"Members",     value:faction.members.toLocaleString(),            icon:<Users     style={{width:11,height:11}}/>},
                ].map(s=>(
                  <div key={s.label} style={{background:"rgba(0,0,0,.2)",borderRadius:6,padding:"8px",textAlign:"center"}}>
                    <div style={{color:faction.color,marginBottom:3,display:"flex",justifyContent:"center"}}>{s.icon}</div>
                    <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:12,color:faction.id==="alnurayn"?"#ffffff":faction.color,fontWeight:700,textShadow:faction.id==="alnurayn"?"0 0 2px #000, 0 0 6px #000, 0 0 10px #000, 2px 2px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 3px 0 0 #000, -3px 0 0 #000, 0 3px 0 #000, 0 -3px 0 #000":undefined}}>{s.value}</div>
                    <div style={{fontSize:9,color:"var(--muted)",fontFamily:"'Cinzel',serif",letterSpacing:.5}}>{s.label}</div>
                  </div>
                ))
            }
          </div>

          {/* Roster */}
          <div>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:9,letterSpacing:3,color:faction.color,textTransform:"uppercase",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
              <Users style={{width:10,height:10}}/> Roster
            </div>
            {loading ? (
              <div style={{fontSize:11,color:"var(--muted)",opacity:.5,padding:"4px 0"}}>Loading roster…</div>
            ) : sortedRoster.length === 0 ? (
              <div style={{fontSize:11,color:"var(--muted)",opacity:.5,padding:"4px 0"}}>No members on record.</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:220,overflowY:"auto"}}>
                {sortedRoster.map((m,i)=>{
                  const rk = (m.rank??"MEMBER").toUpperCase();
                  return (
                    <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",borderRadius:6,background:"rgba(0,0,0,.25)",border:`1px solid ${faction.color}18`}}>
                      <img
                        src={`https://crafthead.net/avatar/${m.uuid}/26`}
                        alt={m.playerName}
                        onError={e=>{e.currentTarget.onerror=null;e.currentTarget.src="https://mc-heads.net/avatar/MHF_Steve/26";}}
                        style={{width:26,height:26,borderRadius:5,flexShrink:0,imageRendering:"pixelated",border:`1px solid ${faction.color}30`}}
                      />
                      <div style={{flex:1,fontFamily:"'Cinzel',serif",fontSize:11,color:"var(--text)",fontWeight:600}}>{m.playerName}</div>
                      <div style={{display:"flex",alignItems:"center",gap:4,color:faction.color,fontSize:9,fontFamily:"'Cinzel',serif",letterSpacing:1,textTransform:"uppercase",flexShrink:0}}>
                        {RANK_ICON[rk] ?? <Flag style={{width:10,height:10}}/>}
                        {RANK_LABEL[rk] ?? m.rank}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Store package card ─────────────────────────────────────────────────────── */
function StorePackage({pkg, onBuy}) {
  return (
    <div style={{background:pkg.colorBg,border:`1px solid ${pkg.colorBorder}`,borderRadius:12,padding:"18px 16px",position:"relative",transition:".2s",display:"flex",flexDirection:"column"}}
      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow=`0 8px 32px ${pkg.color}18`;}}
      onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="";}}>
      {pkg.badge&&<div style={{position:"absolute",top:-10,right:12,background:pkg.badgeColor,color:"#0a0a0f",fontSize:9,fontWeight:800,fontFamily:"'Cinzel',serif",letterSpacing:1,padding:"3px 10px",borderRadius:20,textTransform:"uppercase"}}>{pkg.badge}</div>}
      <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:14}}>
        <div style={{fontSize:20,width:40,height:40,borderRadius:8,background:`${pkg.color}18`,border:`1px solid ${pkg.colorBorder}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          {pkg.isCoin?"🪙":pkg.id==="noble"?"🛡️":pkg.id==="lord"?"⚜️":"👑"}
        </div>
        <div>
          <div style={{fontFamily:"'Cinzel',serif",fontSize:14,fontWeight:700,color:pkg.color}}>{pkg.name}</div>
          <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:18,color:"var(--text)",fontWeight:700,lineHeight:1.2}}>{pkg.price}</div>
        </div>
      </div>
      <ul style={{listStyle:"none",padding:0,margin:"0 0 14px",flex:1}}>
        {pkg.perks.map(p=>(
          <li key={p} style={{display:"flex",alignItems:"center",gap:7,marginBottom:5}}>
            <div style={{width:4,height:4,borderRadius:"50%",background:pkg.color,flexShrink:0}}/>
            <span style={{fontSize:11,color:"var(--text2)"}}>{p}</span>
          </li>
        ))}
      </ul>
      <button onClick={()=>onBuy(pkg)} style={{width:"100%",padding:"9px",borderRadius:8,background:`linear-gradient(135deg,${pkg.color}cc,${pkg.color}88)`,border:"none",color:"#0a0a0f",fontFamily:"'Cinzel',serif",fontSize:11,fontWeight:700,letterSpacing:1,textTransform:"uppercase",cursor:"pointer",transition:".15s"}}
        onMouseOver={e=>e.currentTarget.style.opacity=".85"} onMouseOut={e=>e.currentTarget.style.opacity="1"}>
        Buy Now
      </button>
    </div>
  );
}

/* ── Feature image ─────────────────────────────────────────────────────────── */
function FeatureImg({feat}) {
  return (
    <div style={{position:"relative",borderRadius:10,overflow:"hidden",border:`1px solid ${feat.color}30`,boxShadow:`0 0 0 1px rgba(0,0,0,.6),0 20px 60px rgba(0,0,0,.6),0 0 40px ${feat.color}10`,aspectRatio:"16/9"}}>
      <img src={feat.image} alt={feat.title} style={{width:"100%",height:"100%",objectFit:"cover",filter:"sepia(.15) contrast(1.05)",display:"block"}}/>
      <div style={{position:"absolute",inset:0,background:"radial-gradient(ellipse at center,transparent 40%,rgba(0,0,0,.55) 100%),linear-gradient(to bottom,rgba(0,0,0,.2) 0%,transparent 40%,transparent 60%,rgba(0,0,0,.4) 100%)"}}/>
      <div style={{position:"absolute",inset:0,background:`linear-gradient(135deg,${feat.color}08,transparent)`}}/>
      <svg style={{position:"absolute",top:8,left:8,opacity:.5}} width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 16 L2 2 L16 2" stroke="#c9a84c" strokeWidth="1.5"/></svg>
      <svg style={{position:"absolute",bottom:8,right:8,opacity:.5}} width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M16 2 L16 16 L2 16" stroke="#c9a84c" strokeWidth="1.5"/></svg>
    </div>
  );
}

/* ── About tab ─────────────────────────────────────────────────────────────── */
function AboutTab({onApply}) {
  return (
    <div>
      {/* Hero */}
      <div style={{position:"relative",borderRadius:16,overflow:"hidden",marginBottom:28,minHeight:210,background:"linear-gradient(135deg,#160808 0%,#0a1a0a 50%,#08080f 100%)",border:"1px solid rgba(201,168,76,.15)",boxShadow:"0 0 60px rgba(0,0,0,.6)"}}>
        <div style={{position:"absolute",inset:0,backgroundImage:"url(https://theonesabove.com/gallery/falling.png)",backgroundSize:"cover",backgroundPosition:"center 30%",opacity:.18,filter:"sepia(.4)"}}/>
        <div style={{position:"absolute",inset:0,background:"linear-gradient(to right,rgba(10,5,5,.96) 0%,rgba(10,5,5,.72) 55%,transparent 100%)"}}/>
        <div style={{position:"absolute",left:0,top:0,bottom:0,width:3,background:"linear-gradient(to bottom,transparent,#c9a84c,transparent)"}}/>
        <div style={{position:"relative",zIndex:1,padding:"34px 34px 30px"}}>
          <div style={{fontFamily:"'Cinzel',serif",fontSize:9,letterSpacing:6,color:"#c9a84c",textTransform:"uppercase",marginBottom:10,opacity:.8}}>⚜ The Ones Above — Minecraft</div>
          <h1 style={{fontFamily:"'Cinzel Decorative',serif",fontSize:"clamp(20px,4.5vw,42px)",fontWeight:900,lineHeight:1.05,color:"#f0d878",textShadow:"0 0 40px rgba(201,168,76,.4),0 2px 0 rgba(0,0,0,.8)",marginBottom:12}}>
            Survival. Factions.<br/>Roleplay. Legend.
          </h1>
          <p style={{fontFamily:"'IM Fell English',serif",fontStyle:"italic",fontSize:14,color:"rgba(212,204,232,.7)",maxWidth:420,lineHeight:1.75,marginBottom:20}}>
            A medieval historical roleplay server where three ancient orders clash for dominion. Every choice shapes history. Choose your faction. Write your legacy.
          </p>
        </div>
      </div>

      {/* Server stat pills */}
      <div style={{display:"flex",flexWrap:"wrap",gap:10,marginBottom:32}}>
        {[
          {label:"Game Mode",  value:"Survival",          color:"#c0392b"},
          {label:"Factions",   value:"3 Active Orders",   color:"#c9a84c"},
          {label:"Versions",   value:"Java 26.2",    color:"#60a5fa"},
          {label:"Economy",    value:"Player-Driven",     color:"#27ae60"},
        ].map(s=>(
          <div key={s.label} style={{background:`${s.color}0f`,border:`1px solid ${s.color}30`,borderRadius:8,padding:"10px 16px",flex:"1 1 130px"}}>
            <div style={{fontSize:9,fontFamily:"'Cinzel',serif",letterSpacing:3,color:s.color,textTransform:"uppercase",marginBottom:4}}>{s.label}</div>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:12,fontWeight:700,color:"var(--text)"}}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Features */}
      <div>
        {FEATURES.map((feat,idx)=>{
          const hasImg=!!feat.image, isLeft=feat.imgLeft;
          return (
            <div key={feat.id} style={{padding:"32px 0",borderBottom:idx<FEATURES.length-1?"1px solid rgba(201,168,76,.07)":"none"}}>
              <div style={{fontFamily:"'Cinzel',serif",fontSize:8,letterSpacing:5,color:feat.color,textTransform:"uppercase",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:18,height:1,background:feat.color,opacity:.5}}/>{feat.eyebrow}<div style={{width:18,height:1,background:feat.color,opacity:.5}}/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:hasImg?"1fr 1fr":"1fr",gap:26,alignItems:"center"}} className="mc-feat-grid">
                {hasImg&&isLeft&&<FeatureImg feat={feat}/>}
                <div>
                  <h2 style={{fontFamily:"'Cinzel Decorative',serif",fontSize:"clamp(16px,3vw,24px)",fontWeight:900,lineHeight:1.1,color:feat.color,textShadow:`0 0 30px ${feat.color}40`,marginBottom:12}}>{feat.title}</h2>
                  <p style={{fontFamily:"'IM Fell English',serif",fontStyle:"italic",fontSize:14,color:"rgba(212,204,232,.7)",lineHeight:1.85,marginBottom:16}}>{feat.desc}</p>
                  <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
                    {feat.pills.map(p=>(
                      <span key={p} style={{fontFamily:"'Cinzel',serif",fontSize:9,letterSpacing:2,textTransform:"uppercase",padding:"4px 12px",borderRadius:2,background:`${feat.color}12`,border:`1px solid ${feat.color}30`,color:feat.color}}>{p}</span>
                    ))}
                  </div>
                </div>
                {hasImg&&!isLeft&&<FeatureImg feat={feat}/>}
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <div style={{marginTop:44,textAlign:"center",padding:"36px 24px",background:"linear-gradient(135deg,rgba(201,168,76,.06),rgba(10,8,16,.8))",border:"1px solid rgba(201,168,76,.12)",borderRadius:16}}>
        <div style={{fontFamily:"'Cinzel',serif",fontSize:9,letterSpacing:6,color:"#c9a84c",textTransform:"uppercase",marginBottom:10}}>Your Allegiance Awaits</div>
        <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:"clamp(17px,3.5vw,24px)",color:"#f0d878",marginBottom:10,fontWeight:900}}>Ready to Join the Order?</div>
        <p style={{fontFamily:"'IM Fell English',serif",fontStyle:"italic",fontSize:13,color:"rgba(168,156,200,.8)",maxWidth:340,margin:"0 auto 22px",lineHeight:1.75}}>Three destinies. One world to conquer. Your faction is not merely a team — it is your bloodline, your creed, your legacy.</p>
        <button type="button" onClick={()=>onApply?.()} style={{display:"inline-flex",alignItems:"center",gap:8,cursor:"pointer",fontFamily:"'Cinzel',serif",fontSize:10,letterSpacing:3,padding:"12px 28px",borderRadius:4,background:"linear-gradient(135deg,rgba(201,168,76,.2),rgba(201,168,76,.08))",border:"1px solid rgba(201,168,76,.4)",color:"#f0d878",textTransform:"uppercase",fontWeight:700}}>
          <BookOpen style={{width:12,height:12}}/> Apply Now
        </button>
      </div>
    </div>
  );
}

/* ── Rules tab ─────────────────────────────────────────────────────────────── */
function RulesTab() {
  const sections = [
    {
      title:"Core Conduct",
      icon:"⚖",
      color:"#f0d878",
      intro:"Respect the community. The server is 16+ and roleplay never excuses hate, harassment or explicit content.",
      rules:[
        ["Minimum age: 16+", "You must be at least 16 years old to play on The Ones Above Minecraft server. Players found to be under the minimum age may have their access removed."],
        ["Respect other players", "Harassment, discrimination, hate speech, targeted abuse, threats, slurs and excessive toxicity are not permitted. In-character conflict is fine; genuine personal abuse is not."],
        ["Respect religion", "Religion may be part of factions, characters and server lore, but real-world religions must be treated respectfully. Religious hate speech, slurs, mockery, degrading comments, or deliberate disrespect towards a religion, its beliefs, scriptures, prophets, places of worship or followers are prohibited both in and out of character. Faction rivalry is never an excuse for religious hatred."],
        ["No NSFW activities", "Sexual or pornographic roleplay, explicit sexual conversations, sexual acts, explicit builds or imagery, and other NSFW activity are not permitted on the server. Do not use roleplay as an excuse for sexual harassment or explicit content."],
        ["Stay in character", "During an active RP scene, remain in character where reasonably possible. If a rule dispute happens, finish the scene and report it afterwards rather than turning it into an OOC argument."],
        ["Use common sense", "Not every possible exploit or disruptive behaviour can be written word-for-word. If something clearly exists to gain an unfair advantage or ruin RP, staff may intervene."],
      ],
    },
    {
      title:"Roleplay Standards",
      icon:"🎭",
      color:"#9b7fd4",
      intro:"Your character should act on what they know, value their life and allow other players to respond.",
      rules:[
        ["No metagaming", "Do not use information from Discord, streams, private messages, another character or another player's screen unless your character learned it legitimately in-game."],
        ["No powergaming", "Do not force outcomes on other players, ignore restraints or injuries, perform impossible actions, or use Minecraft mechanics to bypass RP consequences."],
        ["No FailRP", "FailRP means acting in a way that breaks the logic or continuity of the server's world, or deliberately undermines an active roleplay scene. The Ones Above is fantasy RP, so magic, unusual factions and other fantastical elements are valid when they fit the server's lore, mechanics and setting. FailRP is not simply 'something unrealistic'; examples include ignoring an active scene, disregarding established consequences or injuries, breaking character purely to escape a situation, or abusing Minecraft mechanics to avoid roleplay."],
        ["Value your life", "React appropriately when your character is in genuine danger. Do not behave suicidally simply because you know you can respawn."],
        ["New Life Rule", "After dying, do not immediately return to the same conflict for revenge or use knowledge of your death to continue the same fight."],
      ],
    },
    {
      title:"PvP, Crime & Robbery",
      icon:"⚔",
      color:"#f87171",
      intro:"PvP is available for creative RP, but violence must have context and crime has location restrictions.",
      rules:[
        ["No random deathmatch", "Do not attack or kill another player without a valid RP reason. PvP being enabled does not make every player a valid target."],
        ["City PvP", "PvP may occur in the city when a believable RP situation leads to it. The city must not become a constant combat zone or deathmatch area."],
        ["Robbery areas", "Robberies are only allowed in designated/permitted robbery areas. You may not rob players anywhere you choose, and you must give the victim a reasonable opportunity to respond."],
        ["No combat logging", "Do not disconnect to avoid combat, robbery, arrest, pursuit, prison processing, turf conflict or active medical RP."],
        ["No spawn or safe-area abuse", "Do not spawn-kill or use protected boundaries purely to escape, attack safely, or manipulate an active scene."],
      ],
    },
    {
      title:"Factions & Turf",
      icon:"🏰",
      color:"#60a5fa",
      intro:"Alnurayn, the Knights Templars, the Rothschilds and future factions should create stories — not endless on-sight fighting.",
      rules:[
        ["No faction wars inside the city", "Organised faction warfare, raids, large faction fights and turf wars must take place outside the city or in authorised faction/turf areas."],
        ["Faction membership is not KOS", "Being part of an opposing faction is not, by itself, permission to attack someone on sight. There must still be RP context."],
        ["Turf conflict", "Compete for designated turfs through the intended system. Do not repeatedly attack areas simply to grief or prevent others from playing."],
        ["Faction responsibility", "Faction leaders are expected to make sure their members understand and follow the server rules."],
      ],
    },
    {
      title:"City Services & Prison",
      icon:"🛡",
      color:"#4ade80",
      intro:"City Guards and City Emergency Service are RP roles, not NPC mechanics to be ignored or farmed.",
      rules:[
        ["City Guards", "Allow Guards a reasonable opportunity to conduct police RP. You may flee, resist, lie or commit crimes where appropriate, but do not repeatedly bait Guards just to create chases."],
        ["Arrest & prison", "If arrested, participate in the handcuff, escort, booking and prison process. Do not disconnect or exploit bugs to avoid the consequences."],
        ["City Emergency Service", "Roleplay serious injuries and the downed/revival system properly. Do not instantly act fully healthy after revival or attack medical staff without a strong RP reason."],
      ],
    },
    {
      title:"Money, Jobs & Businesses",
      icon:"💰",
      color:"#c9a84c",
      intro:"The economy is part of RP. What you carry and how you work can have consequences.",
      rules:[
        ["Pocket vs Bank", "Money in your Pocket represents cash carried by your character and can be vulnerable during permitted RP. Money deposited in the Bank is not ordinary street-robbery cash."],
        ["Jobs & shops", "While working in places such as coffee shops, stay reasonably in character and do not abuse employee permissions, steal unlimited stock or deliberately sabotage the workplace without valid RP."],
        ["Casino", "Do not exploit casino mechanics, duplicate winnings, block machines/tables or use the casino as an excuse to ignore normal PvP and robbery rules."],
        ["No economy exploits", "Do not duplicate currency/items, abuse trading interfaces, use fake system messages, or exploit bugs during transactions. Report exploits to staff."],
        ["Future illegal systems", "Drugs, contraband and other illegal systems may receive additional rules when they launch. Do not invent mechanics or bypass systems that are not yet live."],
      ],
    },
    {
      title:"Fair Play & Enforcement",
      icon:"🔒",
      color:"#94a3b8",
      intro:"Play fairly, keep characters separate and follow staff directions when intervention is necessary.",
      rules:[
        ["No cheats or unfair modifications", "Cheats and unfair client modifications are prohibited. This includes KillAura, reach, flight hacks, speed hacks, X-ray, combat automation, inventory automation that provides an unfair advantage, packet exploits, duplication exploits and similar tools. Use the official TOA client mods where available rather than third-party alternatives. TOA Minimap is the only minimap mod permitted on the server; all other minimap mods are banned. Other quality-of-life mods may only be used if they are explicitly approved by staff. If you are unsure whether a mod is allowed, ask staff before using it."],
        ["Do not abuse bugs", "Do not exploit bugs for money, items, PvP advantages, prison escapes, protected-area access or faction advantages."],
        ["Alternate accounts & characters", "Do not use alts to avoid punishments, farm resources or transfer knowledge. Separate characters should not automatically share money, enemies, criminal knowledge or faction information."],
        ["Do not impersonate staff", "Never pretend to be a moderator, administrator or developer, or create fake staff/system messages."],
        ["Follow staff instructions", "If staff intervene, follow reasonable instructions. Dispute a decision later through the proper report or appeal process rather than derailing an active scene."],
      ],
    },
  ];

  return (
    <div>
      <div style={{marginBottom:22}}>
        <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:22,color:"var(--text)",marginBottom:4}}>Server Rules</div>
      </div>

      <div style={{padding:"14px 16px",marginBottom:18,borderRadius:10,background:"rgba(201,168,76,.06)",border:"1px solid rgba(201,168,76,.2)",display:"flex",gap:11,alignItems:"flex-start"}}>
        <Info style={{width:17,height:17,color:"#f0d878",flexShrink:0,marginTop:1}}/>
        <div style={{fontSize:12,color:"rgba(235,226,203,.9)",lineHeight:1.7}}>
          These rules exist to keep The Ones Above immersive, fair and enjoyable. Roleplay should create stories rather than simply create ways to win.
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:14}}>
        {sections.map((section) => (
          <div key={section.title} style={{background:"rgba(255,255,255,.025)",border:`1px solid ${section.color}24`,borderRadius:12,overflow:"hidden"}}>
            <div style={{padding:"14px 16px 12px",borderBottom:`1px solid ${section.color}1f`,background:`${section.color}08`}}>
              <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:5}}>
                <span style={{fontSize:18,lineHeight:1}}>{section.icon}</span>
                <div style={{fontFamily:"'Cinzel',serif",fontSize:13,fontWeight:700,color:section.color,letterSpacing:.5}}>{section.title}</div>
              </div>
              <div style={{fontSize:11,color:"var(--muted)",lineHeight:1.55}}>{section.intro}</div>
            </div>
            <div style={{padding:"6px 16px 12px"}}>
              {section.rules.map(([name, text], index) => (
                <div key={name} style={{padding:"11px 0",borderBottom:index===section.rules.length-1?"none":"1px solid rgba(255,255,255,.055)"}}>
                  <div style={{fontFamily:"'Cinzel',serif",fontSize:11,fontWeight:700,color:"var(--text)",marginBottom:4}}>{name}</div>
                  <div style={{fontSize:11.5,color:"var(--text2)",lineHeight:1.65}}>{text}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div style={{marginTop:18,padding:"16px 18px",borderRadius:10,background:"rgba(74,222,128,.04)",border:"1px solid rgba(74,222,128,.14)",textAlign:"center"}}>
        <div style={{fontFamily:"'Cinzel',serif",fontSize:11,color:"#4ade80",letterSpacing:1,marginBottom:5}}>PLAY THE STORY, NOT THE SYSTEM</div>
        <div style={{fontSize:11.5,color:"var(--muted)",lineHeight:1.65}}>Rules may be expanded as new systems such as contraband and additional criminal activities are introduced. Serious or repeated violations may result in warnings, prison consequences, removal from roles/factions, suspension or a permanent ban.</div>
      </div>
    </div>
  );
}

/* ── Support tab ───────────────────────────────────────────────────────────── */
function SupportTab() {
  return (
    <div>
      <div style={{marginBottom:22}}>
        <div style={{fontFamily:"'Cinzel',serif",fontSize:10,letterSpacing:5,color:"#4ade80",textTransform:"uppercase",marginBottom:4}}>Need Help?</div>
        <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:22,color:"var(--text)",marginBottom:4}}>Support</div>
        <div style={{fontSize:13,color:"var(--muted)"}}>Reach our team through Discord or open a ticket.</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:14}}>
        {[
          {icon:<DiscordIcon size={22} color="#818cf8"/>,title:"Join Discord",desc:"Announcements, live support, giveaways, and connect with thousands of other players.",color:"#818cf8",colorBg:"rgba(88,101,242,.07)",colorBorder:"rgba(88,101,242,.2)",href:"https://discord.gg/AzrauJnuqw",label:"Join Discord →"},
          {icon:<TicketSVG size={22} color="#c9a84c"/>,title:"Open a Ticket",desc:"Got an issue, ban appeal, or purchase problem? Open a support ticket and a staff member will assist you.",color:"#c9a84c",colorBg:"rgba(201,168,76,.07)",colorBorder:"rgba(201,168,76,.2)",href:"https://discord.gg/AzrauJnuqw",label:"Go to Discord →"},
        ].map(card=>(
          <div key={card.title} style={{background:card.colorBg,border:`1px solid ${card.colorBorder}`,borderRadius:12,padding:"22px 20px"}}>
            <div style={{width:48,height:48,borderRadius:12,background:`${card.color}14`,border:`1px solid ${card.colorBorder}`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:14}}>{card.icon}</div>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:14,fontWeight:700,color:card.color,marginBottom:8}}>{card.title}</div>
            <p style={{fontSize:13,color:"var(--text2)",lineHeight:1.7,marginBottom:14}}>{card.desc}</p>
            <a href={card.href} target="_blank" rel="noreferrer" style={{fontSize:11,fontFamily:"'Cinzel',serif",color:card.color,textDecoration:"none",letterSpacing:1,transition:".15s"}} onMouseEnter={e=>e.currentTarget.style.opacity=".7"} onMouseLeave={e=>e.currentTarget.style.opacity="1"}>{card.label}</a>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Sidebar ───────────────────────────────────────────────────────────────── */
function McSidebar({onCopyIP, onOpenMcStore, showToast, onOpenForum}) {
  const {minecraft, loaded} = useServerStatus();
  const mcOnline = loaded ? minecraft.online : null;
  const [expandedUpdate, setExpandedUpdate] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(true);
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentPostsLoading, setRecentPostsLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/forum/categories/minecraft/threads?page=1&limit=3&tag=mc-updates`)
      .then(r => r.json()).then(d => setUpdates(d.threads || []))
      .catch(() => setUpdates([]))
      .finally(() => setUpdatesLoading(false));
    fetch(`${API}/forum/categories/minecraft/threads?page=1&limit=3`)
      .then(r => r.json()).then(d => setRecentPosts(d.threads || []))
      .catch(() => setRecentPosts([]))
      .finally(() => setRecentPostsLoading(false));
  }, []);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>

      {/* Server status */}
      <div style={cardStyle}>
        <div style={{...cardHead, justifyContent:"space-between"}}>
          <span style={{display:"flex",alignItems:"center",gap:7}}>
            <span style={{fontSize:10}}>⛏</span> Server Status
          </span>
          <span style={{display:"flex",alignItems:"center",gap:5}}>
            <span style={{width:7,height:7,borderRadius:"50%",display:"inline-block",background:mcOnline===null?"#888":mcOnline?"#4ade80":"#ef4444",boxShadow:mcOnline?"0 0 6px #4ade80":"none"}}/>
            <span style={{color:mcOnline===null?"var(--muted)":mcOnline?"#4ade80":"#ef4444"}}>
              {mcOnline===null?"…":mcOnline?"Live":"Offline"}
            </span>
          </span>
        </div>
        <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:10}}>
          {[
            {label:"Players", value:loaded?`${minecraft.playerCount}/${minecraft.maxPlayers}`:"—", color:"#4ade80"},
            {label:"Mode",    value:"Survival",                                                      color:"var(--text2)"},
          ].map(r=>(
            <div key={r.label} style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span style={{fontSize:12,color:"var(--text2)",fontFamily:"'Cinzel',serif"}}>{r.label}</span>
              <span style={{fontSize:12,color:r.color,fontFamily:"'Cinzel',serif",fontWeight:700}}>{r.value}</span>
            </div>
          ))}
          <div style={{display:"flex",gap:5,justifyContent:"flex-end"}}>
  <span
    style={{
      fontSize:9,
      padding:"2px 8px",
      borderRadius:20,
      fontFamily:"'Cinzel',serif",
      background:"rgba(245,158,11,.09)",
      border:"1px solid rgba(245,158,11,.2)",
      color:"#f59e0b"
    }}
  >
    Java Edition
  </span>
</div>
          <div onClick={onCopyIP} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 11px",borderRadius:7,background:"rgba(74,222,128,.05)",border:"1px solid rgba(74,222,128,.14)",cursor:"pointer",marginTop:2,transition:".15s"}}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(74,222,128,.11)"}
            onMouseLeave={e=>e.currentTarget.style.background="rgba(74,222,128,.05)"}>
            <ClipboardCopy style={{width:11,height:11,color:"#4ade80",flexShrink:0}}/>
            <span style={{fontFamily:"'Courier New',monospace",fontSize:11,color:"#4ade80"}}>play.theonesabove.com</span>
          </div>
        </div>
      </div>

      {/* MC Store */}
      <div style={cardStyle}>
        <div style={cardHead}>
          <ShoppingBag style={{width:12,height:12}}/> TOA MC Store
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gridTemplateRows:"auto auto",gap:7,padding:10}}>
          <div className="toa-scard toa-pu" style={{gridColumn:1,gridRow:1,padding:"10px 8px"}} onClick={()=>onOpenMcStore?.("ranks")}>
            <div style={{animation:"scard-crown-float2 2.8s ease-in-out infinite",display:"inline-block"}}><Crown style={{width:20,height:20,color:"var(--purpleB)"}}/></div>
            <div className="toa-scard-n" style={{fontSize:9,marginTop:5}}>Ranks</div>
          </div>
          <div className="toa-scard toa-go" style={{gridColumn:1,gridRow:2,padding:"10px 8px"}} onClick={()=>onOpenMcStore?.("coins")}>
            <div style={{animation:"scard-coin-float2 2.5s ease-in-out infinite",display:"inline-block"}}>
              <img src="/toa-coin_mc.png" alt="TOA Coin" style={{width:22,height:22,objectFit:"contain",display:"block",margin:"0 auto",filter:"drop-shadow(0 0 5px rgba(201,168,76,.55))"}}/>
            </div>
            <div className="toa-scard-n" style={{fontSize:9,marginTop:5}}>TOA Coins</div>
          </div>
          <div className="toa-scard toa-cr" style={{gridColumn:2,gridRow:"1 / 3",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:6,padding:"10px 8px"}} onClick={()=>showToast?.("Crates — coming soon!")}>
            <Package style={{width:22,height:22,color:"#f87171"}}/>
            <div className="toa-scard-n" style={{fontSize:9}}>Crates</div>
          </div>
        </div>
      </div>

      {/* Server Updates */}
      <div style={cardStyle}>
        <div style={{...cardHead, gap:7}}>
          <Bell style={{width:12,height:12,color:"var(--gold,#c9a84c)"}}/> Server Updates
        </div>
        {updatesLoading && [1,2,3].map(i=>(
          <div key={i} className="toa-thread" style={{gap:10}}>
            <div style={{width:28,height:28,borderRadius:6,background:"rgba(201,168,76,.1)",flexShrink:0}}/>
            <div style={{flex:1}}><div style={{height:9,borderRadius:4,background:"rgba(255,255,255,.06)",marginBottom:6,width:"70%"}}/><div style={{height:8,borderRadius:4,background:"rgba(255,255,255,.04)",width:"45%"}}/></div>
          </div>
        ))}
        {!updatesLoading && updates.length===0 && (
          <div style={{padding:"14px 16px",fontSize:12,color:"var(--muted)",fontStyle:"italic"}}>No updates yet.</div>
        )}
        {!updatesLoading && updates.map((t,idx)=>(
          <div key={t.id}>
            <div className="toa-thread" style={{cursor:"pointer"}} onClick={()=>setExpandedUpdate(expandedUpdate===idx?null:idx)}>
              <div className="toa-ti-avatar" style={{background:"linear-gradient(135deg,#1a1428,#0d0a1e)",border:"1px solid rgba(201,168,76,.2)",overflow:"hidden"}}>
                <svg viewBox="0 0 28 28" width="28" height="28" fill="none"><circle cx="14" cy="10.5" r="4.5" fill="rgba(201,168,76,0.5)"/><path d="M4 24c0-5.523 4.477-9 10-9s10 3.477 10 9" fill="rgba(201,168,76,0.3)"/></svg>
              </div>
              <div className="toa-ti-body">
                <span className="toa-ti-name">{t.title}</span>
                <div className="toa-ti-meta">By: <span>{t.author}</span> · {relTime(t.created_at)}</div>
              </div>
              <ChevronRight style={{width:12,height:12,flexShrink:0,color:"var(--muted)",transform:expandedUpdate===idx?"rotate(90deg)":"none",transition:".2s"}}/>
            </div>
            {expandedUpdate===idx&&(
              <div style={{margin:"0 12px 8px",padding:"10px 12px",background:"rgba(201,168,76,.04)",border:"1px solid rgba(201,168,76,.12)",borderRadius:8,fontSize:12,color:"var(--text2)",lineHeight:1.65,cursor:"pointer"}}
                onClick={()=>onOpenForum?.("minecraft","mc-updates")}>
                {t.body ? t.body.slice(0,200)+(t.body.length>200?"…":"") : "Click to read full update."}
              </div>
            )}
          </div>
        ))}
        <div style={{padding:"10px 16px",borderTop:"1px solid rgba(255,255,255,.06)",textAlign:"center"}}>
          <button onClick={()=>onOpenForum?.("minecraft","mc-updates")} className="toa-btn toa-btn-p" style={{fontSize:10,padding:"7px 16px",display:"inline-flex",alignItems:"center",gap:5,cursor:"pointer"}}>All Updates <ArrowRight style={{width:11,height:11}}/></button>
        </div>
      </div>

      {/* Recent Minecraft forum posts */}
      <div style={cardStyle}>
        <div style={{...cardHead, gap:7}}>
          <MessageSquare style={{width:12,height:12,color:"#4ade80"}}/> Recent Posts
        </div>
        {recentPostsLoading && [1,2,3].map(i=>(
          <div key={i} className="toa-thread" style={{gap:10}}>
            <div style={{width:28,height:28,borderRadius:6,background:"rgba(74,222,128,.1)",flexShrink:0}}/>
            <div style={{flex:1}}><div style={{height:9,borderRadius:4,background:"rgba(255,255,255,.06)",marginBottom:6,width:"70%"}}/><div style={{height:8,borderRadius:4,background:"rgba(255,255,255,.04)",width:"45%"}}/></div>
          </div>
        ))}
        {!recentPostsLoading && recentPosts.length===0 && (
          <div style={{padding:"14px 16px",fontSize:12,color:"var(--muted)",fontStyle:"italic"}}>No forum posts yet.</div>
        )}
        {!recentPostsLoading && recentPosts.map(t=>(
          <button key={t.id} className="toa-thread" style={{background:"none",border:"none",cursor:"pointer",textAlign:"left",width:"100%"}}
            onClick={()=>onOpenForum?.("minecraft",null,t.slug || String(t.id))}>
            <div className="toa-ti-avatar" style={{background:"linear-gradient(135deg,#102016,#09110c)",border:"1px solid rgba(74,222,128,.2)",overflow:"hidden"}}>
              <svg viewBox="0 0 28 28" width="28" height="28" fill="none"><circle cx="14" cy="10.5" r="4.5" fill="rgba(74,222,128,0.5)"/><path d="M4 24c0-5.523 4.477-9 10-9s10 3.477 10 9" fill="rgba(74,222,128,0.3)"/></svg>
            </div>
            <div className="toa-ti-body">
              <span className="toa-ti-name">{t.title}</span>
              <div className="toa-ti-meta">By: <span>{t.author}</span> · {relTime(t.last_post_at || t.created_at)}</div>
            </div>
            <ChevronRight style={{width:12,height:12,flexShrink:0,color:"var(--muted)"}}/>
          </button>
        ))}
        <div style={{padding:"10px 16px",borderTop:"1px solid rgba(255,255,255,.06)",textAlign:"center"}}>
          <button onClick={()=>onOpenForum?.("minecraft")} className="toa-btn toa-btn-g" style={{fontSize:10,padding:"7px 16px",display:"inline-flex",alignItems:"center",gap:5,cursor:"pointer"}}>View Minecraft Forums <ArrowRight style={{width:11,height:11}}/></button>
        </div>
      </div>

      {/* Staff Activity — horizontal row with real skin images */}
      <div style={cardStyle}>
        <div style={cardHead}>
          <Users style={{width:12,height:12}}/> Staff Activity
        </div>
        <div style={{padding:"14px 12px 16px",display:"flex",gap:8,justifyContent:"space-around"}}>
          {RECENT_STAFF.map(s=>(
            <div key={s.name} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,flex:"1 1 0",minWidth:0}}>
              <div style={{position:"relative"}}>
                <img src={s.skinUrl} alt={s.name}
                  onError={e=>{e.target.style.display="none";e.target.nextSibling.style.display="flex";}}
                  style={{width:40,height:40,borderRadius:6,border:`2px solid ${s.roleColor}44`,display:"block"}}/>
                <div style={{width:40,height:40,borderRadius:6,border:`2px solid ${s.roleColor}44`,background:`${s.roleColor}14`,display:"none",alignItems:"center",justifyContent:"center",fontFamily:"'Cinzel',serif",fontSize:16,color:s.roleColor}}>{s.name[0]}</div>
                <div style={{position:"absolute",bottom:-3,right:-3,width:9,height:9,borderRadius:"50%",background:s.lastSeen==="Online"?"#4ade80":"#888",border:"2px solid rgba(8,14,8,1)",boxShadow:s.lastSeen==="Online"?"0 0 5px #4ade80":"none"}}/>
              </div>
              <div style={{textAlign:"center",width:"100%"}}>
                <div style={{fontFamily:"'Cinzel',serif",fontSize:9,fontWeight:700,color:"var(--text)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.name}</div>
                <div style={{fontSize:8,color:s.roleColor,letterSpacing:.3,fontFamily:"'Cinzel',serif"}}>{s.role}</div>
                <div style={{fontSize:8,color:"var(--muted)",marginTop:1}}>{s.lastSeen}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Support */}
      <div style={cardStyle}>
        <div style={{...cardHead, gap:7}}>
          <TicketSVG size={12} color="rgba(168,156,200,.55)"/> Support
        </div>
        <div style={{padding:"14px 16px 16px",display:"flex",flexDirection:"column",gap:8}}>
          <p style={{fontSize:12,color:"var(--muted)",lineHeight:1.65,margin:0}}>Need help? Reach us on Discord or open a support ticket — staff respond within 24h.</p>
          <a href="https://discord.gg/AzrauJnuqw" target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"10px 0",borderRadius:8,background:"rgba(88,101,242,.12)",border:"1px solid rgba(88,101,242,.3)",color:"#818cf8",textDecoration:"none",fontFamily:"'Cinzel',serif",fontSize:11,fontWeight:700,letterSpacing:1,transition:".15s"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(88,101,242,.25)"} onMouseLeave={e=>e.currentTarget.style.background="rgba(88,101,242,.12)"}>
            <DiscordIcon size={14} color="#818cf8"/> Join Discord
          </a>
          <a href="https://discord.gg/AzrauJnuqw" target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"10px 0",borderRadius:8,background:"rgba(201,168,76,.08)",border:"1px solid rgba(201,168,76,.25)",color:"#c9a84c",textDecoration:"none",fontFamily:"'Cinzel',serif",fontSize:11,fontWeight:700,letterSpacing:1,transition:".15s"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(201,168,76,.18)"} onMouseLeave={e=>e.currentTarget.style.background="rgba(201,168,76,.08)"}>
            <TicketSVG size={14} color="#c9a84c"/> Open a Ticket
          </a>
        </div>
      </div>

    </div>
  );
}

/* ── Tab config ────────────────────────────────────────────────────────────── */
const TABS = [
  {id:"about",       label:"Overview",  icon:<Info       style={{width:13,height:13}}/>},
  {id:"rules",       label:"Rules",     icon:<ScrollText style={{width:13,height:13}}/>},
  {id:"map",         label:"World Map", icon:<Map        style={{width:13,height:13}}/>},
  {id:"factions",    label:"Factions",  icon:<Flag       style={{width:13,height:13}}/>},
  {id:"leaderboard", label:"Rankings",  icon:<Trophy     style={{width:13,height:13}}/>},
  {id:"mods",        label:"Mods",      icon:<Package    style={{width:13,height:13}}/>},
];

/* ── Unified Minecraft top navigation ─────────────────────────────────────── */
function MinecraftTopNav({ tab, switchTab, onGoHome, onOpenAuth, onOpenSettings, onOpenAdmin, hidden = false }) {
  const { authUser, logout } = useAuth();
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const dropdownItem = {
    width:"100%", display:"flex", alignItems:"center", gap:10,
    padding:"9px 14px", background:"transparent", border:"none",
    color:"#d7d9dd", cursor:"pointer", fontSize:11,
    fontFamily:"'Cinzel',serif", letterSpacing:.5, textAlign:"left",
  };

  const openForum = () => window.location.assign("https://forums.theonesabove.com/minecraft");

  const mobileNavItem = {
    width:"100%", display:"flex", alignItems:"center", gap:10,
    padding:"12px 14px", border:"none", borderRadius:7,
    background:"transparent", color:"var(--text2)", cursor:"pointer",
    fontFamily:"'Cinzel',serif", fontSize:12, fontWeight:600,
    letterSpacing:1.1, textTransform:"uppercase", textAlign:"left",
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <div className="mc-unified-nav" style={{
        position:"sticky", top:0, zIndex:120,
        display:hidden?"none":"grid",
        gridTemplateColumns:"minmax(170px,1fr) auto minmax(170px,1fr)",
        alignItems:"center", gap:14,
        minHeight:62, padding:"0 40px",
        background:"rgba(6,16,8,.97)", backdropFilter:"blur(18px)",
        borderBottom:"1px solid rgba(74,222,128,.14)",
        boxShadow:"0 1px 30px rgba(0,0,0,.55)",
      }}>
        <div className="mc-nav-brand" style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
          <button className="mc-mobile-menu-btn" onClick={()=>setMobileOpen(v=>!v)} aria-label="Minecraft menu" style={{display:"none"}}>
            <Menu style={{width:20,height:20}}/>
          </button>
          <a href="https://theonesabove.com/" className="toa-logo mc-brand-button"
            onClick={(e)=>{e.preventDefault();onGoHome?.();}}>
            The Ones Above
          </a>
        </div>

        <div className="mc-unified-tabs" style={{
          display:"flex",gap:2,background:"rgba(0,0,0,.4)",
          border:"1px solid rgba(74,222,128,.1)",borderRadius:8,padding:3,
          overflowX:"auto",maxWidth:"100%",
        }}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>switchTab(t.id)} style={{
              display:"flex",alignItems:"center",gap:5,padding:"7px 11px",borderRadius:6,
              border:"none",cursor:"pointer",background:tab===t.id?"rgba(74,222,128,.17)":"transparent",
              color:tab===t.id?"#4ade80":"var(--muted)",fontFamily:"'Cinzel',serif",
              fontSize:10,fontWeight:600,letterSpacing:.8,textTransform:"uppercase",
              transition:".15s",whiteSpace:"nowrap",
              boxShadow:tab===t.id?"inset 0 0 12px rgba(74,222,128,.08)":"none",
            }}>
              {t.icon}<span className="mc-tab-label">{t.label}</span>
            </button>
          ))}
        </div>

        <div className="mc-nav-actions" style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:8,minWidth:0}}>
          {authUser ? (
            <div ref={dropRef} style={{position:"relative"}} className="mc-profile-desktop">
              <button onClick={()=>setDropOpen(v=>!v)} style={{
                display:"flex",alignItems:"center",gap:7,padding:"6px 9px",
                border:"1px solid rgba(74,222,128,.16)",borderRadius:8,
                background:"rgba(255,255,255,.025)",color:"#e9ecef",cursor:"pointer",
                fontFamily:"'Cinzel',serif",fontSize:11,
              }}>
                <span style={{width:24,height:24,borderRadius:"50%",display:"grid",placeItems:"center",background:"linear-gradient(135deg,#3d7f49,#20552c)"}}>
                  <User style={{width:12,height:12,color:"#fff"}}/>
                </span>
                <span className="mc-profile-name">{authUser.username}</span>
                <ChevronDown style={{width:12,height:12,opacity:.65,transform:dropOpen?"rotate(180deg)":"none",transition:".18s"}}/>
              </button>

              {dropOpen && <div style={{
                position:"absolute",right:0,top:"calc(100% + 8px)",minWidth:205,
                background:"linear-gradient(160deg,#101c12,#080d09)",
                border:"1px solid rgba(74,222,128,.17)",borderRadius:10,overflow:"hidden",
                boxShadow:"0 16px 48px rgba(0,0,0,.75)",zIndex:300,
              }}>
                <div style={{padding:"11px 14px",borderBottom:"1px solid rgba(255,255,255,.06)"}}>
                  <div style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"#f2f2f2",fontWeight:600}}>{authUser.username}</div>
                  <div style={{fontSize:10,color:"var(--muted)",marginTop:2}}>{authUser.role?.toUpperCase()}</div>
                </div>
                <div style={{padding:"5px 0"}}>
                  <button style={dropdownItem} onClick={()=>{setDropOpen(false);onOpenSettings?.();}}><Settings style={{width:13,height:13}}/>Settings</button>
                  {authUser.role === "admin" && <button style={{...dropdownItem,color:"#c9a84c"}} onClick={()=>{setDropOpen(false);onOpenAdmin?.();}}><LayoutDashboard style={{width:13,height:13}}/>Admin Dashboard</button>}
                  <div style={{height:1,background:"rgba(255,255,255,.06)",margin:"4px 0"}}/>
                  <button style={{...dropdownItem,color:"#f87171"}} onClick={()=>{setDropOpen(false);logout();}}><LogOut style={{width:13,height:13}}/>Log Out</button>
                </div>
              </div>}
            </div>
          ) : (
            <>
              <button className="toa-btn toa-btn-ghost mc-auth-login" onClick={()=>onOpenAuth?.("login")}>Log In</button>
              <button className="toa-btn toa-btn-p mc-auth-join" onClick={()=>onOpenAuth?.("signup")}>Join Us</button>
            </>
          )}
        </div>
      </div>

      {mobileOpen && !hidden && <div className="mc-mobile-overlay" onClick={closeMobile}/>} 
      <aside className={`mc-mobile-drawer ${mobileOpen && !hidden ? "open" : ""}`}>
        <div className="mc-mobile-drawer-head">
          <a href="https://theonesabove.com/" className="toa-logo" onClick={(e)=>{e.preventDefault();closeMobile();onGoHome?.();}}>The Ones Above</a>
          <button className="mc-mobile-close" onClick={closeMobile} aria-label="Close menu"><X style={{width:18,height:18}}/></button>
        </div>
        <div className="mc-mobile-links">
          {TABS.map((t,i)=><Fragment key={t.id}>
            <button style={{...mobileNavItem,color:tab===t.id?"#4ade80":"var(--text2)",background:tab===t.id?"rgba(74,222,128,.08)":"transparent"}} onClick={()=>{switchTab(t.id);closeMobile();}}>{t.icon}{t.label}</button>
            {t.id === "rules" && <button style={mobileNavItem} onClick={()=>{closeMobile();openForum();}}><MessageSquare style={{width:13,height:13}}/>Forums</button>}
          </Fragment>)}
        </div>
        {authUser && <div className="mc-mobile-account">
          <div className="mc-mobile-user">
            <span>{authUser.username}</span>
            {authUser.role !== "member" && <span style={{fontSize:10,padding:"2px 7px",borderRadius:12,background:authUser.role === "admin" ? "rgba(201,168,76,.15)" : "rgba(123,94,167,.15)",border:`1px solid ${authUser.role === "admin" ? "rgba(201,168,76,.3)" : "rgba(123,94,167,.3)"}`,color:authUser.role === "admin" ? "var(--gold)" : "var(--purpleB)"}}>{authUser.role.toUpperCase()}</span>}
          </div>
          <button className="toa-btn toa-btn-ghost" style={{width:"100%"}} onClick={()=>{logout();closeMobile();}}><LogOut style={{width:13,height:13}}/> Log Out</button>
        </div>}
      </aside>
    </>
  );
}


function ModsTab() {
  const mods = [
    {
      name: "TOA HUD Mod",
      badge: "Minecraft 26.2",
      description: "Customise and improve the server HUD, including health bars, skill displays, spacing, text sizing and other HUD presentation options.",
      href: "https://forums.theonesabove.com/thread/toa-hud-mod",
      icon: <Monitor style={{width:24,height:24,color:"#4ade80"}}/>,
    },
    {
      name: "Simple Voice Chat",
      badge: "Fabric",
      description: "Enable proximity voice chat in-game so you can speak naturally with nearby players while exploring, roleplaying and playing on TOA RP.",
      href: "https://forums.theonesabove.com/thread/simple-voice-chat",
      icon: <Mic style={{width:24,height:24,color:"#4ade80"}}/>,
    },
  ];

  return (
    <div>
      <div style={{marginBottom:22}}>
        <div style={{fontFamily:"'Cinzel',serif",fontSize:10,letterSpacing:5,color:"#4ade80",textTransform:"uppercase",marginBottom:5}}>Client Downloads</div>
        <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:22,color:"var(--text)",marginBottom:6}}>The Ones Above Mods</div>
        <div style={{fontSize:13,color:"var(--muted)",lineHeight:1.7,maxWidth:720}}>Optional client-side mods supported for The Ones Above. Choose a mod below to open its installation guide and download instructions.</div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:14,maxWidth:980}}>
        {mods.map(mod => (
          <a key={mod.name} href={mod.href} style={{textDecoration:"none",display:"block"}}>
            <div
              style={{
                ...cardStyle,
                height:"100%",
                padding:22,
                cursor:"pointer",
                transition:"transform .18s,border-color .18s,background .18s",
                border:"1px solid rgba(74,222,128,.2)",
                background:"linear-gradient(145deg,rgba(74,222,128,.055),rgba(255,255,255,.018))",
              }}
              onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.borderColor="rgba(74,222,128,.42)";}}
              onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.borderColor="rgba(74,222,128,.2)";}}
            >
              <div style={{display:"flex",gap:16,alignItems:"flex-start"}}>
                <div style={{width:50,height:50,borderRadius:11,display:"grid",placeItems:"center",flexShrink:0,background:"rgba(74,222,128,.10)",border:"1px solid rgba(74,222,128,.22)"}}>
                  {mod.icon}
                </div>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:9,flexWrap:"wrap",marginBottom:7}}>
                    <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:17,color:"var(--text)",fontWeight:700}}>{mod.name}</div>
                    <span style={{fontFamily:"'Cinzel',serif",fontSize:9,letterSpacing:1.3,textTransform:"uppercase",color:"#4ade80",padding:"3px 7px",borderRadius:20,border:"1px solid rgba(74,222,128,.22)",background:"rgba(74,222,128,.07)"}}>{mod.badge}</span>
                  </div>
                  <div style={{fontSize:12.5,color:"var(--text2)",lineHeight:1.65,marginBottom:14}}>{mod.description}</div>
                  <div style={{display:"flex",alignItems:"center",gap:7,fontFamily:"'Cinzel',serif",fontSize:10,fontWeight:600,letterSpacing:.8,textTransform:"uppercase",color:"#4ade80"}}>
                    <Download style={{width:13,height:13}}/>View Mod & Installation Guide<ExternalLink style={{width:11,height:11,marginLeft:2}}/>
                  </div>
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

export default function MinecraftPage({onClose, onOpenMcStore, onCopyIP, showToast, initialTab="about", onTabChange, onOpenForum, onOpenAuth, onOpenSettings, onOpenAdmin, onApply}) {
  const [tab, setTab] = useState(initialTab === "stats" ? "leaderboard" : initialTab);

  // Keep tab in sync if parent navigates (e.g. browser back/forward)
  useEffect(() => { setTab(initialTab === "stats" ? "leaderboard" : initialTab); }, [initialTab]);

  // SEO title
  useEffect(() => { document.title = "TOA RP"; return () => { document.title = "The Ones Above"; }; }, []);

  const switchTab = (id) => {
    setTab(id);
    // expose "stats" in URL instead of internal id "leaderboard"
    if (onTabChange) onTabChange(id === "leaderboard" ? "stats" : id);
  };
  const [expandedFaction, setExpandedFaction] = useState(null);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [leaderboardModalOpen, setLeaderboardModalOpen] = useState(false);

  return (
    <div style={{minHeight:"100vh",position:"relative",overflow:"hidden"}}>

      {/* ── Page background ── */}
      <div style={{position:"fixed",inset:0,zIndex:0,pointerEvents:"none",background:"linear-gradient(180deg,#0a1a0a 0%,#061008 40%,#040c10 100%)"}}/>

      {/* Background image — full-page texture at very low opacity */}
      <div style={{position:"fixed",inset:0,zIndex:0,pointerEvents:"none",backgroundImage:MC_BG_IMAGE?`url(${MC_BG_IMAGE})`:"none",backgroundSize:"cover",backgroundPosition:"center",backgroundAttachment:"fixed",opacity:.06,mixBlendMode:"luminosity",filter:"saturate(0.3)"}}/>

      {/* Atmospheric glow */}
      <div style={{position:"fixed",inset:0,zIndex:0,pointerEvents:"none"}}>
        <div style={{position:"absolute",top:"8%", left:"-5%",  width:500,height:500,borderRadius:"50%",background:"radial-gradient(circle,rgba(74,222,128,.06) 0%,transparent 70%)", filter:"blur(40px)"}}/>
        <div style={{position:"absolute",top:"55%",right:"-8%",  width:550,height:550,borderRadius:"50%",background:"radial-gradient(circle,rgba(192,57,43,.05) 0%,transparent 70%)",  filter:"blur(50px)"}}/>
        <div style={{position:"absolute",bottom:"8%",left:"28%", width:380,height:380,borderRadius:"50%",background:"radial-gradient(circle,rgba(155,127,212,.05) 0%,transparent 70%)",filter:"blur(40px)"}}/>
      </div>

      <style>{`
        @keyframes scard-coin-float2{0%,100%{transform:translateY(0) rotate(-4deg)}50%{transform:translateY(-6px) rotate(4deg)}}
        @keyframes scard-crown-float2{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
        @keyframes mc-float-a{0%,100%{transform:translateY(0) rotate(12deg)}50%{transform:translateY(-18px) rotate(18deg)}}
        @keyframes mc-float-b{0%,100%{transform:translateY(0) rotate(-8deg)}50%{transform:translateY(-12px) rotate(-14deg)}}
        .mc-feat-grid{}
        @media(max-width:640px){.mc-feat-grid{grid-template-columns:1fr!important}}
        .mc-mobile-overlay{display:none}.mc-mobile-drawer{display:none}
        @media(max-width:900px){
          .mc-unified-nav{display:flex!important;justify-content:space-between!important;padding:0 16px!important;gap:10px!important}
          .mc-unified-tabs{display:none!important}
          .mc-auth-join{display:none!important}
          .mc-mobile-menu-btn{display:flex!important;align-items:center;justify-content:center;width:34px;height:34px;padding:0;background:transparent;border:1px solid rgba(74,222,128,.18);border-radius:7px;color:var(--text2);cursor:pointer}
          .mc-nav-actions{gap:7px!important}
          .mc-auth-login{order:1;padding:6px 10px!important;font-size:10px!important}
          .mc-profile-desktop{order:1}
          .mc-mobile-overlay{display:block;position:fixed;inset:0;background:rgba(0,0,0,.58);backdrop-filter:blur(3px);z-index:198}
          .mc-mobile-drawer{display:flex;position:fixed;top:0;left:0;bottom:0;width:min(320px,86vw);background:linear-gradient(160deg,#0d1810,#070b08);border-right:1px solid rgba(74,222,128,.18);z-index:199;flex-direction:column;transform:translateX(-100%);transition:transform .26s cubic-bezier(.16,1,.3,1);box-shadow:18px 0 50px rgba(0,0,0,.48)}
          .mc-mobile-drawer.open{transform:translateX(0)}
          .mc-mobile-drawer-head{height:62px;display:flex;align-items:center;justify-content:space-between;padding:0 18px;border-bottom:1px solid rgba(255,255,255,.07)}
          .mc-mobile-close{width:32px;height:32px;display:grid;place-items:center;background:transparent;border:1px solid rgba(74,222,128,.17);border-radius:7px;color:var(--muted);cursor:pointer}
          .mc-mobile-links{padding:14px 12px;display:flex;flex-direction:column;gap:2px;overflow:auto}
          .mc-mobile-account{margin-top:auto;padding:14px 16px 18px;border-top:1px solid rgba(255,255,255,.07)}
          .mc-mobile-user{display:flex;align-items:center;justify-content:space-between;gap:8px;color:var(--text);font-family:'Cinzel',serif;font-size:12px;padding:8px 4px 12px}
        }
        @media(max-width:520px){.mc-unified-nav{padding:0 12px!important}.mc-brand-button{font-size:16px!important}.mc-profile-name{display:none}}
        @media(max-width:900px){
          .mc-page-grid{grid-template-columns:1fr!important}
          .mc-sidebar-desktop{display:none!important}
          .mc-sidebar-mobile{display:block!important}
        }
      `}</style>

      {/* Floating block decorations */}
      {[
        {top:"8%", left:"2%",  size:36,color:"#4ade80",anim:"mc-float-a 7s ease-in-out infinite",     opacity:.10},
        {top:"22%",right:"1%", size:26,color:"#c9a84c",anim:"mc-float-b 9s ease-in-out infinite 1s",  opacity:.08},
        {top:"58%",left:"1%",  size:20,color:"#c0392b",anim:"mc-float-a 6s ease-in-out infinite 2s",  opacity:.09},
        {top:"72%",right:"2%", size:30,color:"#9b7fd4",anim:"mc-float-b 8s ease-in-out infinite .5s", opacity:.08},
      ].map((b,i)=>(
        <div key={i} style={{position:"fixed",zIndex:0,pointerEvents:"none",top:b.top,left:b.left,right:b.right,width:b.size,height:b.size,background:`linear-gradient(135deg,${b.color}88,${b.color}44)`,border:`1px solid ${b.color}55`,borderRadius:3,animation:b.anim,opacity:b.opacity,boxShadow:`0 0 10px ${b.color}28`}}/>
      ))}

      <MinecraftTopNav
        tab={tab}
        switchTab={switchTab}
        onGoHome={onClose}
        onOpenAuth={onOpenAuth}
        onOpenSettings={onOpenSettings}
        onOpenAdmin={onOpenAdmin}
        hidden={leaderboardModalOpen}
      />

      {/* ── Two-column layout ── */}
      <div style={{maxWidth:1200,margin:"0 auto",padding:"28px 20px 60px",position:"relative",zIndex:1,display:"grid",gridTemplateColumns:mapFullscreen?"1fr":"1fr 280px",gap:24,alignItems:"start",transition:"grid-template-columns .3s ease"}} className="mc-page-grid">

        {/* Main content */}
        <div>
          {tab==="about"       &&<AboutTab onApply={onApply}/>}
          {tab==="rules"       &&<RulesTab/>}

          {tab==="map"&&(
            <DynMapSection onFullscreenChange={setMapFullscreen} />
          )}

          {tab==="factions"&&(
            <FactionsTab expandedFaction={expandedFaction} setExpandedFaction={setExpandedFaction}/>
          )}

          {tab==="mods"       &&<ModsTab/>}

          {tab==="leaderboard"&&(
            <div>
              <div style={{marginBottom:20}}>
                <div style={{fontFamily:"'Cinzel',serif",fontSize:10,letterSpacing:5,color:"#4ade80",textTransform:"uppercase",marginBottom:4}}>Server Rankings</div>
                <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:22,color:"var(--text)",marginBottom:4}}>Player Statistics</div>
                <div style={{fontSize:13,color:"var(--muted)"}}>Search for any player, or browse top rankings by category.</div>
              </div>
              <Leaderboard onModalChange={setLeaderboardModalOpen}/>
            </div>
          )}

          {/* Mobile sidebar — stacks below main content on narrow screens */}
          <div className="mc-sidebar-mobile" style={{display:"none",marginTop:32}}>
            <McSidebar onCopyIP={onCopyIP} onOpenMcStore={onOpenMcStore} showToast={showToast} onOpenForum={onOpenForum}/>
          </div>
        </div>

        {/* Desktop sidebar */}
        <div className="mc-sidebar-desktop" style={{position:"sticky",top:68,display:mapFullscreen?"none":"block",transition:"opacity .3s"}}>
          <McSidebar onCopyIP={onCopyIP} onOpenMcStore={onOpenMcStore} showToast={showToast} onOpenForum={onOpenForum}/>
        </div>
      </div>
    </div>
  );
}